// src/components/Experiment/AnalysisDialog.js
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Row, Col, Alert, Badge, Accordion, InputGroup, Tabs, Tab, Collapse } from 'react-bootstrap';
import { estimateAnalysis } from '../../api/photoProcessingApi';
import HelpLink from '../HelpLink';
import { METHOD_DOCS } from '../../constants/docsLinks';
import './AnalysisDialog.css';

// Docs pages the "?" buttons of the dialog open
const ANALYSIS_DIALOG_DOC = '/chemometrics/running-analysis/analysis-dialog';
const VALIDATION_DOC = '/chemometrics/running-analysis/validation';
const NESTED_DOC = '/chemometrics/running-analysis/nested-validation';
const FEATURE_SELECTION_DOC = '/chemometrics/running-analysis/feature-selection';
const REPLICATES_DOC = '/chemometrics/running-analysis/replicates';
const HARD_SOFT_PLSDA_DOC = '/chemometrics/explanation/hard-vs-soft-plsda';

// Analysis types that can be validated by nested cross-validation (backend: nested_cv.NESTED_TYPES)
const NESTED_TYPES = ['lda', 'logistic', 'svm', 'random_forest', 'knn', 'simca', 'plsda', 'xgboost', 'bbc', 'brf'];

// '40 s', '12 min', '2.5 h'
const formatDuration = (seconds) => {
  if (seconds < 90) return `${Math.max(1, Math.round(seconds))} s`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} min`;
  return `${(seconds / 3600).toFixed(1)} h`;
};

const AnalysisDialog = ({ show, onHide, analysisType, tableData, experimentId, onSubmit, onStartRangeSelection }) => {
  const [config, setConfig] = useState({
    name: '',
    data_range: '',
    label_column: '',
    parameters: {},
    cv_method: 'group',
    cv_folds: 5,
    is_classes_enumerating: false,
  });
  const [errors, setErrors] = useState({});
  const [previewData, setPreviewData] = useState(null);
  const [labelStats, setLabelStats] = useState(null);
  const [previousConfig, setPreviousConfig] = useState(null);
  const [activeKey, setActiveKey] = useState('0'); // For tab navigation
  // State variables (add these with your other state declarations)
  const [includedLabels, setIncludedLabels] = useState(new Set());
  const [originalRange, setOriginalRange] = useState('');
  // Column (feature) filtering. null means "no manual selection yet -> all columns included",
  // to avoid a race with the initial data load where an empty Set would look like "deselect all".
  const [includedColumns, setIncludedColumns] = useState(null);
  const [isColumnFilterExpanded, setIsColumnFilterExpanded] = useState(false);
  // Last data_range string produced by rebuildDataRange (i.e. by the label/column filters).
  // null means the current data_range was written from outside (manual input, table
  // selection, dialog reset). Lets effects tell "derived from selections" from "typed by user".
  const internalRangeRef = useRef(null);
  // Last automatically computed values of the parameters that depend on the number of data
  // columns. A parameter still equal to its last auto value is considered untouched and follows
  // the range; anything the user changed is left alone.
  const autoParamsRef = useRef({ n_components: null, search_max_n: null });
  // Number of features (k) of the Simple validation mode, kept while the mode is Nested
  // (k is then 'auto') and restored when the user switches back
  const simpleKRef = useRef('all');
  // Estimate of the request (number of analyses, model fits, time) from the server
  const [estimate, setEstimate] = useState(null);
  const [showNestedAdvanced, setShowNestedAdvanced] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (show && analysisType) {
      const defaultDataRange = getDefaultDataRange();
      const defaultLabelColumn = getDefaultLabelColumn();
      // Starting range: the default one without rows that have no label in the default label column
      let startRange = defaultDataRange;
      try {
        if (defaultDataRange && defaultLabelColumn) {
          const labels = new Set(Object.keys(getLabelCounts(defaultDataRange, defaultLabelColumn)));
          const filtered = computeFilteredRange(defaultDataRange, labels, null, defaultLabelColumn);
          if (filtered && parseDataRanges(filtered).rows.length < parseDataRanges(defaultDataRange).rows.length) {
            startRange = filtered;
          }
        }
      } catch (error) {
        console.error('Could not drop unlabeled rows from the default range:', error);
      }

      // Calculate default CV settings
      let defaultCvMethod = 'group';
      let defaultCvFolds = 5;
      console.log("Calc ls sample_name default "+defaultDataRange);
      const stats = calculateLabelStats("sample_name", startRange);

      const isClassColumn = defaultLabelColumn &&
        tableData.columns_metadata[defaultLabelColumn]?.type === 'class';
      const isSampleColumn = defaultLabelColumn === 'sample_name';
      if (isClassColumn && stats && stats.uniqueCount > 0) {
        defaultCvMethod = 'group';
        defaultCvFolds = stats.uniqueCount;
      } else if (isSampleColumn && stats?.totalCount) {
        defaultCvMethod = 'kfold';
        defaultCvFolds = Math.floor(stats.totalCount / 6);
      }

      // Use previous config if available, otherwise use defaults
      const newConfig = previousConfig ? {
        ...previousConfig,
        name: `${getAnalysisDisplayName(analysisType)} Analysis`,
        data_range: startRange,
        label_column: defaultLabelColumn,
        parameters: getDefaultParameters(analysisType, defaultDataRange)
      } : {
        name: `${getAnalysisDisplayName(analysisType)} Analysis`,
        data_range: startRange,
        label_column: defaultLabelColumn,
        parameters: getDefaultParameters(analysisType, defaultDataRange),
        cv_method: defaultCvMethod,
        cv_folds: defaultCvFolds
      };

      setConfig(newConfig);
      autoParamsRef.current = {
        n_components: newConfig.parameters?.n_components ?? null,
        search_max_n: newConfig.parameters?.search_optimal_n_components?.max_n ?? null
      };
      setErrors({});
      setPreviewData(null);
      setActiveKey('0'); // Reset to first tab
      setIncludedColumns(null); // Reset column filter to "all columns"
      setIsColumnFilterExpanded(false);
      simpleKRef.current = 'all';
      setEstimate(null);
      setShowNestedAdvanced(false);

      // Explicit initialisation of the filter base (previously a hidden side effect of
      // calculateLabelStats): the default range is the "original" range, all its labels are included
      internalRangeRef.current = startRange !== defaultDataRange ? startRange : null;
      setOriginalRange(defaultDataRange);
      setIncludedLabels(new Set(Object.keys(getLabelCounts(defaultDataRange, defaultLabelColumn))));
    }
  }, [analysisType, ]);

  useEffect(() => {
    if (!show && config.name) {
      setPreviousConfig(config);
    }
  }, [show, config]);

  // Data range changed: user-configured algorithm parameters must survive. Only the values that
  // are derived from the number of data columns (PLS-DA/SIMCA n_components, the upper bound of the
  // n_components search) are refreshed, and only while the user has not changed them.
  // An empty/invalid range leaves parameters untouched. Everything else (kernel, C, feature
  // selection, GridSearch config, included_classes, ...) is never reset here - a full reset
  // happens only when the dialog is (re)opened for an analysis type (reset effect above).
  useEffect(() => {
    if (!config.data_range) return;

    let columnCount;
    try {
      columnCount = parseDataRanges(config.data_range).dataColumns.length;
    } catch {
      return;
    }

    const newAuto = {
      n_components: Math.min(columnCount, 7),
      search_max_n: Math.min(columnCount, 10)
    };
    const prevAuto = autoParamsRef.current;
    autoParamsRef.current = newAuto;

    setConfig(prevConfig => {
      const params = prevConfig.parameters || {};
      const nextParams = { ...params };
      let changed = false;

      if ((analysisType === 'plsda' || analysisType === 'simca') &&
          params.n_components === prevAuto.n_components &&
          params.n_components !== newAuto.n_components) {
        nextParams.n_components = newAuto.n_components;
        changed = true;
      }

      const search = params.search_optimal_n_components;
      if (search && typeof search === 'object' &&
          search.max_n === prevAuto.search_max_n &&
          search.max_n !== newAuto.search_max_n) {
        nextParams.search_optimal_n_components = { ...search, max_n: newAuto.search_max_n };
        changed = true;
      }

      return changed ? { ...prevConfig, parameters: nextParams } : prevConfig;
    });
  }, [config.data_range]);

  // Auto-update CV settings based on label column
  useEffect(() => {
    if (config.label_column && config.data_range && analysisType !== 'pca' && analysisType !== 'pipeline') {
      const isClassColumn = tableData.columns_metadata[config.label_column]?.type === 'class';
      const isSampleColumn = config.label_column === 'sample_name';

      let newCvMethod = config.cv_method;
      let newCvFolds = config.cv_folds;
      console.log("Calc ls sample_name "+config.data_range);
      const stats = calculateLabelStats("sample_name", config.data_range);

      if (isClassColumn && stats && stats.uniqueCount > 0) {
        newCvMethod = 'group';
        newCvFolds = stats.uniqueCount;
      } else if (isSampleColumn && stats?.totalCount) {
        newCvMethod = 'kfold';
        newCvFolds = Math.floor(stats.totalCount / 6);
      }

      if (newCvMethod !== config.cv_method || newCvFolds !== config.cv_folds) {
        setConfig(prev => ({
          ...prev,
          cv_method: newCvMethod,
          cv_folds: newCvFolds
        }));
      }
    }
  }, [config.label_column, config.data_range, analysisType, tableData]);

  // ----- Validation mode (Simple / Nested) -----
  const validationParams = config.parameters?.validation || {};
  const isNestedMode = validationParams.mode === 'nested';
  const supportsNested = NESTED_TYPES.includes(analysisType) || analysisType === 'all';
  const fsEnabledForNested = !!config.parameters?.feature_selection?.enabled;
  const searchParams = config.parameters?.search_optimal_n_components;
  // 'all' always includes PLS-DA with the search of the number of components
  const componentSearchOn = analysisType === 'all' ||
    (analysisType === 'plsda' && !!searchParams && typeof searchParams === 'object');
  // Nested validation needs something to choose inside the folds
  const canUseNested = supportsNested && (fsEnabledForNested || componentSearchOn);

  const setValidationParams = (changes) => {
    setConfig(prev => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        validation: { ...(prev.parameters?.validation || {}), ...changes }
      }
    }));
  };

  // Nothing left to choose (feature selection / component search switched off): back to Simple
  useEffect(() => {
    if (isNestedMode && !canUseNested) {
      setValidationParams({ mode: 'simple' });
    }
  }, [isNestedMode, canUseNested]);

  // Nested: the number of features is chosen automatically (k: 'auto'); the k of the Simple
  // mode is remembered and comes back with the mode
  useEffect(() => {
    setConfig(prev => {
      const params = prev.parameters || {};
      const fs = params.feature_selection;
      if (!fs || analysisType === 'pipeline') return prev;
      const nested = params.validation?.mode === 'nested';
      if (nested && fs.enabled && fs.k !== 'auto') {
        simpleKRef.current = fs.k;
        return { ...prev, parameters: { ...params, feature_selection: { ...fs, k: 'auto', k_min: fs.k_min ?? 1, k_max: fs.k_max ?? null } } };
      }
      if (!nested && fs.k === 'auto') {
        const k = typeof simpleKRef.current === 'number' ? simpleKRef.current : 'all';
        return { ...prev, parameters: { ...params, feature_selection: { ...fs, k } } };
      }
      return prev;
    });
  }, [validationParams.mode, config.parameters?.feature_selection?.enabled, config.parameters?.feature_selection?.k]);

  // Estimate of the request (analyses, model fits, time), recalculated on the server when the
  // settings that change the amount of work change. The last request cancels the previous ones;
  // a failed estimate never blocks the start (the server checks the limit itself).
  const estimateKey = JSON.stringify([
    analysisType, config.data_range, config.label_column, config.cv_method, config.cv_folds,
    config.is_classes_enumerating, config.parameters
  ]);
  useEffect(() => {
    if (!show || !experimentId || !config.data_range || !config.label_column) {
      setEstimate(null);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const result = await estimateAnalysis(
          experimentId, { ...buildSubmitConfig(), analysis_type: analysisType }, controller.signal
        );
        setEstimate(result);
      } catch (error) {
        if (!controller.signal.aborted) setEstimate(null);
      }
    }, 600);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [show, experimentId, estimateKey]);

  console.log("Re rendered "+config.data_range);

  // Label of a row as a string, '' for a missing value (null / undefined / ''). Rows without a
  // label cannot be checked in the label filter, so a filtered range never contains them (the
  // backend would treat them as 'unknown' anyway). Must never throw: an unlabeled row (e.g.
  // samples to predict) used to make rebuildDataRange fail silently, so data_range stayed unchanged.
  const getRowLabel = (rowIndex, column) => {
    const value = tableData?.data_matrix?.[rowIndex]?.[column];
    return value === null || value === undefined ? '' : String(value);
  };

  // Pure helper: label -> row count for the rows of `range` (no state writes)
  const getLabelCounts = (range, column) => {
    const counts = {};
    if (!range || !range.trim() || !column || !tableData) return counts;
    try {
      parseDataRanges(range).rows.forEach(rowIndex => {
        const label = getRowLabel(rowIndex, column);
        if (label !== '') {
          counts[label] = (counts[label] || 0) + 1;
        }
      });
    } catch (error) {
      console.error('Error counting labels:', error);
    }
    return counts;
  };

  // Label stats as they follow from the label selection alone (independent of the column
  // selection and of whether data_range is currently empty). includedLabels is the source of truth.
  const buildStatsFromSelection = (stats, labelsSet) => {
    const originalCounts = stats.originalCounts || {};
    const labelCounts = {};
    let totalCount = 0;
    let includedCount = 0;
    Object.entries(originalCounts).forEach(([label, count]) => {
      const included = labelsSet.has(label);
      labelCounts[label] = included ? count : 0;
      if (included) {
        totalCount += count;
        includedCount += 1;
      }
    });
    return {
      ...stats,
      labelCounts,
      totalCount,
      uniqueCount: includedCount > 0 ? includedCount : Object.keys(originalCounts).length,
      hasCurrentData: totalCount > 0
    };
  };

  // Helper functions for label filtering
  const handleLabelToggle = (label) => {
    // Works with an empty data_range too: the range is (re)built from the selections
    if (!labelStats || !config.label_column) {
      return;
    }

    const newIncludedLabels = new Set(includedLabels);
    if (newIncludedLabels.has(label)) {
      newIncludedLabels.delete(label);
    } else {
      newIncludedLabels.add(label);
    }

    setIncludedLabels(newIncludedLabels);
    setLabelStats(buildStatsFromSelection(labelStats, newIncludedLabels));

    // Update data range based on included labels (and the current column selection)
    rebuildDataRange(newIncludedLabels, includedColumns);
  };

  // Label Column changed by the user: the old label selection is meaningless for the new
  // column, so reset it to "all labels" and, if it was actually filtering rows, bring
  // data_range back in line (column selection is kept).
  const handleLabelColumnChange = (newColumn) => {
    const newIncludedLabels = new Set(Object.keys(getLabelCounts(originalRange, newColumn)));

    setIncludedLabels(newIncludedLabels);
    setConfig(prev => ({ ...prev, label_column: newColumn }));

    // Always: rows without a label in the new column must leave the range even if no label was unchecked
    if (originalRange && newColumn) {
      rebuildDataRange(newIncludedLabels, includedColumns, newColumn);
    }
  };

  // Pure: the range that follows from a base range and the row (label) / column (feature) selections.
  // Rows without a label are never included (they belong to no label; the backend would treat them
  // as 'unknown'). includedColumnsSet === null means "all columns". '' = nothing selected.
  const computeFilteredRange = (baseRange, includedLabelsSet, includedColumnsSet, labelColumn) => {
    const baseParsed = parseDataRanges(baseRange);

    const orderedColumns = getOrderedColumns();

    // Rows: filtered by label only when we actually have a label column to filter by
    const rowsToUse = labelColumn
      ? baseParsed.rows.filter(rowIndex => includedLabelsSet.has(getRowLabel(rowIndex, labelColumn)))
      : baseParsed.rows;

    // Columns: null means "no manual filtering yet" -> keep every data column
    const columnsToUse = includedColumnsSet === null
      ? baseParsed.dataColumns
      : baseParsed.dataColumns.filter(col => includedColumnsSet.has(col));

    if (rowsToUse.length === 0 || columnsToUse.length === 0) return '';

    // Group selected rows into contiguous row blocks
    const sortedRows = [...rowsToUse].sort((a, b) => a - b);
    const rowBlocks = [];
    let rowStart = sortedRows[0];
    let rowEnd = sortedRows[0];
    for (let i = 1; i < sortedRows.length; i++) {
      if (sortedRows[i] === rowEnd + 1) {
        rowEnd = sortedRows[i];
      } else {
        rowBlocks.push({ start: rowStart, end: rowEnd });
        rowStart = sortedRows[i];
        rowEnd = sortedRows[i];
      }
    }
    rowBlocks.push({ start: rowStart, end: rowEnd });

    // Group selected columns into contiguous column chunks (by their position on the sheet)
    const colIndices = columnsToUse
      .map(col => orderedColumns.indexOf(col))
      .sort((a, b) => a - b);
    const colChunks = [];
    let colStart = colIndices[0];
    let colEnd = colIndices[0];
    for (let i = 1; i < colIndices.length; i++) {
      if (colIndices[i] === colEnd + 1) {
        colEnd = colIndices[i];
      } else {
        colChunks.push({ start: colStart, end: colEnd });
        colStart = colIndices[i];
        colEnd = colIndices[i];
      }
    }
    colChunks.push({ start: colStart, end: colEnd });

    // Every row block combined with every column chunk (both axes are filtered independently)
    const rangeStrings = [];
    for (const rowBlock of rowBlocks) {
      for (const colChunk of colChunks) {
        const startColLetter = indexToExcelColumn(colChunk.start);
        const endColLetter = indexToExcelColumn(colChunk.end);
        rangeStrings.push(`${startColLetter}${rowBlock.start + 1}:${endColLetter}${rowBlock.end + 1}`);
      }
    }

    return rangeStrings.join(', ');
  };

  // Single writer for config.data_range: derives it from the current row (label)
  // and column (feature) selections together, so the two filters never race to
  // overwrite each other's contribution to the range string.
  // labelColumn defaults to the current one; the Label Column handler passes the new one
  // explicitly because config.label_column is not updated yet at that point.
  const rebuildDataRange = (includedLabelsSet, includedColumnsSet, labelColumn = config.label_column) => {
    console.log("rebuildDataRange: Starting");
    console.log("includedLabelsSet:", Array.from(includedLabelsSet));
    console.log("includedColumnsSet:", includedColumnsSet === null ? 'null (all)' : Array.from(includedColumnsSet));
    console.log("originalRange:", originalRange);
    console.log("config.data_range:", config.data_range);

    try {
      // Use the original range as the base for both rows and columns, not the
      // (possibly already filtered) current data_range
      const baseRange = originalRange || config.data_range;
      console.log("Using baseRange:", baseRange);

      const newRange = computeFilteredRange(baseRange, includedLabelsSet, includedColumnsSet, labelColumn);

      if (newRange === '') {
        // Nothing selected on one of the axes - keep checkboxes visible, but the range is empty
        // (the selections themselves stay intact, so any later click can restore a valid range)
        console.log("No rows or no columns included, setting empty data range");
        internalRangeRef.current = '';
        setConfig(prev => ({ ...prev, data_range: '' }));
        return;
      }
      console.log("New data range to set:", newRange, "length:", newRange.length);

      // A previous "range is empty/required" error no longer applies once the filters produce a range
      setErrors(prev => {
        if (!prev.data_range) return prev;
        const { data_range: _clearedError, ...rest } = prev;
        return rest;
      });

      if (newRange.length > 480) {
        setErrors(prev => ({
          ...prev,
          data_range: `Selected rows/columns combination produces a range specification that is too long (${newRange.length} characters, limit 500). Deselect fewer scattered columns or simplify label filtering.`
        }));
      }

      internalRangeRef.current = newRange;
      setConfig(prev => ({ ...prev, data_range: newRange }));

    } catch (error) {
      console.error('Error rebuilding data range:', error);
      console.error('Error stack:', error.stack);
      // The selection could not be applied: say so instead of silently sending the old range
      setErrors(prev => ({
        ...prev,
        data_range: `The column/label selection could not be applied to the data range (${error.message}). ` +
          'Reopen the dialog or type the range manually.'
      }));
    }

    console.log("rebuildDataRange: Completed");
  };

  // Helper function to check if all labels are included (includedLabels is the source of truth)
  const areAllLabelsIncluded = () => {
    if (!labelStats || !labelStats.originalCounts) {
      return false;
    }
    return Object.keys(labelStats.originalCounts).every(label => includedLabels.has(label));
  };

  // Helper function to toggle all labels
  const handleToggleAllLabels = () => {
    if (!labelStats) {
      return;
    }

    // Select All must cover every label of the original range, not only those currently present
    const newIncludedLabels = areAllLabelsIncluded()
      ? new Set()
      : new Set(Object.keys(labelStats.originalCounts || {}));

    setIncludedLabels(newIncludedLabels);
    setLabelStats(buildStatsFromSelection(labelStats, newIncludedLabels));
    rebuildDataRange(newIncludedLabels, includedColumns);
  };

  const areAllColumnsIncluded = () => {
    return includedColumns === null || includedColumns.size === allDataColumns.length;
  };

  // Helper functions for column filtering
  const handleColumnToggle = (col) => {
    const newIncludedColumns = includedColumns === null
      ? new Set(allDataColumns)
      : new Set(includedColumns);

    if (newIncludedColumns.has(col)) {
      newIncludedColumns.delete(col);
    } else {
      newIncludedColumns.add(col);
    }

    setIncludedColumns(newIncludedColumns);
    rebuildDataRange(includedLabels, newIncludedColumns);
  };

  const handleToggleAllColumns = () => {
    if (allDataColumns.length === 0) return;

    const allIncluded = areAllColumnsIncluded();
    const newIncludedColumns = allIncluded ? new Set() : new Set(allDataColumns);

    setIncludedColumns(newIncludedColumns);
    rebuildDataRange(includedLabels, newIncludedColumns);
  };

  // Keep parameters.included_classes / total_classes in sync with the label selection.
  // includedLabels is the source of truth; the order follows originalCounts (first appearance in
  // the original range), which is the same relative order the backend has always received.
  // Must stay declared AFTER the [config.data_range] parameters reset effect, because that reset
  // wipes these two keys on every range change and this effect has to restore them in the same flush.
  useEffect(() => {
    if (config.label_column && labelStats) {
      const totalClasses = Object.keys(labelStats.originalCounts || {});
      const includedClasses = totalClasses.filter(label => includedLabels.has(label));

      setConfig(prev => ({
        ...prev,
        parameters: {
          ...prev.parameters,
          included_classes: includedClasses,
          total_classes: totalClasses
        }
      }));
    }
  }, [analysisType, config.label_column, labelStats, includedLabels, config.data_range]);

  // Recalculate label stats when the label column or the data range changes.
  // This effect never resets includedLabels for a range produced by the filters (or an empty one):
  // an empty range says nothing about which labels the user selected. Only a range written from
  // outside (manual input / table selection) is used to re-derive the selected labels.
  useEffect(() => {
    if (!config.label_column || (!config.data_range && !originalRange)) {
      setLabelStats(null);
      return;
    }

    const stats = calculateLabelStats(config.label_column, config.data_range);
    if (!stats) {
      setLabelStats(null);
      return;
    }

    const isSelectionDerived = !config.data_range || (
      internalRangeRef.current !== null && config.data_range === internalRangeRef.current
    );

    if (isSelectionDerived) {
      setLabelStats(buildStatsFromSelection(stats, includedLabels));
    } else {
      setLabelStats(stats);
      setIncludedLabels(new Set(
        Object.entries(stats.labelCounts || {})
          .filter(([_, count]) => count > 0)
          .map(([label]) => label)
      ));
    }
  }, [config.label_column, config.data_range]);

  // State to track which stages are included in GridSearch
    const [gridSearchStages, setGridSearchStages] = useState({
      feature_selection: false,
      pls: false,
      classifier: false
    });

    // State for dependent parameter mappings (PLS components based on feature selection k)
    const [dependentParamMapping, setDependentParamMapping] = useState({});

    // State for GridSearch parameter input values (temporary local state)
    const [gridParamInputValues, setGridParamInputValues] = useState({});
    const [dependentPLSInputValues, setDependentPLSInputValues] = useState({});

    // Toggle GridSearch for a specific stage
    const toggleGridSearchStage = (stage) => {
      setGridSearchStages({
        ...gridSearchStages,
        [stage]: !gridSearchStages[stage]
      });
    };

    // Initialize custom grid params if not exist
    useEffect(() => {
      if (!config.parameters.custom_grid_params && config.parameters.grid_search_enabled) {
        setConfig(prev => ({
          ...prev,
          parameters: {
            ...prev.parameters,
            custom_grid_params: {}
          }
        }));
      }
    }, [config.parameters.grid_search_enabled]);

  // Helper function to calculate inner CV defaults
  const calculateInnerCvDefaults = (labelColumn, dataRange) => {
    let defaultInnerCvMethod = 'stratified';
    let defaultInnerCvFolds = 5;

    if (labelColumn && dataRange) {
      console.log("Calc ls sample_name inner cv "+config.data_range);
      const stats = calculateLabelStats('sample_name', dataRange);

      const isClassColumn = labelColumn &&
        tableData.columns_metadata[labelColumn]?.type === 'class';
      const isSampleColumn = labelColumn === 'sample_name';

      if (isClassColumn && stats && stats.uniqueCount > 0) {
        defaultInnerCvMethod = 'group';
        defaultInnerCvFolds =  stats.uniqueCount === 2 ? 2 : (stats.uniqueCount - 1);
      } else if (isSampleColumn) {
        defaultInnerCvMethod = 'kfold';
        defaultInnerCvFolds = Math.max(2, Math.floor(stats.totalCount / 6) - 1);
      }
    }

    return { defaultInnerCvMethod, defaultInnerCvFolds };
  };

  // Update inner CV settings when relevant dependencies change
  useEffect(() => {
    if (config.parameters.grid_search_enabled) {
      const { defaultInnerCvMethod, defaultInnerCvFolds } =
        calculateInnerCvDefaults(config.label_column, config.data_range);

      const currentGridSearchParams = config.parameters.grid_search_params || {};

      // Determine if we should update - only if using defaults or not set
      const shouldUpdateMethod =
        !currentGridSearchParams.inner_cv_method ||
        currentGridSearchParams.inner_cv_method === 'stratified';

      const shouldUpdateFolds =
        !currentGridSearchParams.inner_cv_folds ||
        currentGridSearchParams.inner_cv_folds === 5;

      if (shouldUpdateMethod || shouldUpdateFolds) {
        const updatedGridSearchParams = {
          ...currentGridSearchParams,
          inner_cv_method: shouldUpdateMethod ? defaultInnerCvMethod : currentGridSearchParams.inner_cv_method,
          inner_cv_folds: shouldUpdateFolds ? defaultInnerCvFolds : currentGridSearchParams.inner_cv_folds
        };

        // Functional update: must not overwrite what other effects set in the same flush
        setConfig(prev => ({
          ...prev,
          parameters: {
            ...prev.parameters,
            grid_search_params: {
              ...(prev.parameters.grid_search_params || {}),
              inner_cv_method: updatedGridSearchParams.inner_cv_method,
              inner_cv_folds: updatedGridSearchParams.inner_cv_folds
            }
          }
        }));
      }
    }
  }, [
    config.parameters.grid_search_enabled,
    config.label_column,
    config.data_range,
    tableData
  ]);

  const getAnalysisDisplayName = (type) => {
    const names = {
      'pca': 'PCA',
      'lda': 'LDA',
      'logistic': 'Logistic Regression',
      'linear': 'Linear Regression',
      'svm': 'SVM',
      'random_forest': 'Random Forest',
      'knn': 'K-Nearest Neighbors',
      'plsda': 'PLS-DA',
      'simca': 'SIMCA',
      'pipeline': 'Pipeline Model',
      'xgboost': 'Extreme Gradient Boosting',
      'bbc': 'Balanced Bagging Classifier',
      'brf': 'Balanced Random Forest',
      'all': 'All Types'
    };
    return names[type] || type.toUpperCase();
  };

  const getDefaultDataRange = () => {
    if (!tableData) return '';

    // Find first and last data columns
    const orderedColumns = getOrderedColumns();
    const dataColumns = orderedColumns.filter(colName =>
      tableData.columns_metadata[colName]?.type === 'data'
    );

    if (dataColumns.length === 0) return '';

    const firstDataIndex = orderedColumns.indexOf(dataColumns[0]);
    const lastDataIndex = orderedColumns.indexOf(dataColumns[dataColumns.length - 1]);

    const startCol = indexToExcelColumn(firstDataIndex);
    const endCol = indexToExcelColumn(lastDataIndex);

    return `${startCol}1:${endCol}${tableData.data_matrix.length}`;
  };

  const getDefaultLabelColumn = () => {
    if (!tableData) return '';

    // Prefer class columns, then sample_name
    const classColumns = Object.entries(tableData.columns_metadata)
      .filter(([_, meta]) => meta.type === 'class')
      .map(([name, _]) => name);

    if (classColumns.length > 0) {
      return classColumns[0];
    }

    return 'sample_name';
  };

  const getDefaultParameters = (type, data_range) => {
    let preview = null;
    if (data_range) {
      try {
        preview = parseDataRanges(data_range);
      } catch {
        preview = null;
      }
    }
    const max_n = preview ? Math.min(preview.dataColumns.length, 7) : 7;
    const max_n_search = preview ? Math.min(preview.dataColumns.length, 10) : 10;
    const baseParams = {
      feature_selection: {
        enabled: false,
        method: 'anova',
        k: 'all',
        threshold: 0

      },
      // 'simple' - features are selected once on all data, then cross-validation;
      // 'nested' - the selection is repeated inside every fold (k: 'auto', k_min, k_max)
      validation: {
        mode: 'simple',
        inner_folds: 4,
        selection_rule: 'one_se'
      },
      average_replicates: false,
      search_optimal_n_components: {
        min_n: 2,
        max_n: max_n_search,
        metric: "accuracy"
      }
    };

    switch (type) {
      case 'pca':
        return { ...baseParams, n_components: 5 };
      case 'lda':
        return { ...baseParams, solver: 'svd' };
      case 'logistic':
        return { ...baseParams, max_iter: 1000, C: 1.0 };
      case 'svm':
        return { ...baseParams, kernel: 'rbf', C: 1.0 };
      case 'random_forest':
        return { ...baseParams, n_estimators: 100, max_depth: null };
      case 'knn':
        return { ...baseParams, n_neighbors: 5, weights: 'uniform', metric: 'minkowski', scale_data: true };
      case 'plsda':
        return { ...baseParams, n_components: max_n, style: 'hard', alpha: 0.05, scale_x: true };
      case 'simca':
        return { ...baseParams, n_components: max_n, alpha: 0.05, scale_x: true };
      case 'pipeline':
        return {
          ...baseParams,
          classifier: 'random_forest',
          classifier_params: {},
          pls_n_components: 5,
          pls_max_iter: 500,
          grid_search_enabled: false,
          grid_search_params: {
            scoring: 'f1_weighted',
            inner_cv_method: 'stratified',
            inner_cv_folds: 5
          }
        };
      case 'xgboost':
        return {
          ...baseParams,
          n_estimators: 100,
          learning_rate: 0.1,
          max_depth: 6,
          min_child_weight: 1,
          subsample: 1.0,
          colsample_bytree: 1.0,
          gamma: 0,
          reg_alpha: 0,
          reg_lambda: 1,
          objective: 'multi:softprob',
          eval_metric: 'mlogloss',
          early_stopping_rounds: 10,
          random_state: 42
        };
      case 'bbc':
        return {
          ...baseParams,
          n_estimators: 10,
          max_samples: 1.0,
          max_features: 1.0,
          bootstrap: true,
          bootstrap_features: false,
          random_state: 42,
          sampling_strategy: 'auto',
          replacement: false,
          base_estimator: {
            max_depth: null,
            min_samples_split: 2,
            min_samples_leaf: 1
          }
        };
      case 'brf':
        return {
          ...baseParams,
          n_estimators: 100,
          criterion: 'gini',
          max_depth: null,
          min_samples_split: 2,
          min_samples_leaf: 1,
          max_features: 'sqrt',
          bootstrap: true,
          random_state: 42,
          class_weight: 'balanced_subsample',
          replacement: false,
          sampling_strategy: 'auto'
        };
      default:
        return baseParams;
    }
  };

  // Helper functions for Excel column notation
  const indexToExcelColumn = (index) => {
    let result = '';
    while (index >= 0) {
      result = String.fromCharCode(65 + (index % 26)) + result;
      index = Math.floor(index / 26) - 1;
    }
    return result;
  };

  const excelColumnToIndex = (columnStr) => {
    let index = 0;
    for (let i = 0; i < columnStr.length; i++) {
      index = index * 26 + (columnStr.charCodeAt(i) - 64);
    }
    return index - 1;
  };

  // Convert selected range from DataTable to Excel-like format
  const rangesToExcelFormat = (selectedRanges) => {
    if (!selectedRanges || !selectedRanges.length || !tableData) return '';

    const excelRanges = selectedRanges.map(range => {
      if (!range) return '';

      const orderedColumns = getOrderedColumns();

      // Get column indices
      const startColIndex = orderedColumns.indexOf(range.columnNames[0]);
      const endColIndex = orderedColumns.indexOf(range.columnNames[range.columnNames.length - 1]);

      const startCol = indexToExcelColumn(startColIndex);
      const endCol = indexToExcelColumn(endColIndex);
      const startRow = range.startRow + 1; // Convert to 1-based
      const endRow = range.endRow + 1; // Convert to 1-based

      if (range.startRow === range.endRow && range.columnNames.length === 1) {
        return `${startCol}${startRow}`;
      }

      return `${startCol}${startRow}:${endCol}${endRow}`;
    });

    // Filter out empty ranges and join with commas
    return excelRanges.filter(range => range !== '').join(', ');
  };

  // A range written from outside the filters (typed by the user, selected in the table) becomes the
  // base of the column / label filters: they rebuild data_range from originalRange, so with the old
  // base a later click on a checkbox replaced the user's range by the default (full) one.
  const adoptExternalRange = (rangeStr) => {
    internalRangeRef.current = null;
    setIncludedColumns(null);
    try {
      parseDataRanges(rangeStr);
      setOriginalRange(rangeStr);
    } catch {
      // incomplete input while typing: keep the previous base until the range is valid
    }
  };

  // Handle range selection from DataTable
  const handleRangeSelectionFromTable = () => {
    if (onStartRangeSelection) {
      onStartRangeSelection((selectedRanges) => {
        const excelRanges = rangesToExcelFormat(selectedRanges);
        adoptExternalRange(excelRanges); // written from outside the label/column filters
        setConfig(prev => ({ ...prev, data_range: excelRanges }));

        // Auto-generate preview
        try {
          const preview = parseDataRanges(excelRanges);
          setPreviewData(preview);
          validateDataRange(preview);
        } catch (error) {
          console.error('Error generating preview:', error);
        }
      });
    }
  };

  // Validate data range for specific analysis requirements
  const validateDataRange = (preview) => {
    const newErrors = { ...errors };

    if (!preview || !preview.dataColumns || preview.dataColumns.length === 0) {
      newErrors.data_range = 'Selected range must contain at least one data column';
      setErrors(newErrors);
      return false;
    }

    // Check for classification algorithms
    const classificationAlgorithms = ['lda', 'logistic', 'svm', 'random_forest', 'knn', 'plsda', 'simca', 'pipeline', 'xgboost', 'bbc', 'brf'];
    if (classificationAlgorithms.includes(analysisType)) {
      if (preview.dataColumns.length < 2) {
        newErrors.data_range = `For ${getAnalysisDisplayName(analysisType)}, selected range must contain at least 2 data columns`;
      }
    }

    // Check for PCA component count
    if (analysisType === 'pca' || analysisType === 'pipeline') {
      const nComponents = analysisType === 'pca'
        ? config.parameters.n_components || 5
        : config.parameters.pls_n_components || 5;

      if (nComponents > preview.dataColumns.length) {
        const paramName = analysisType === 'pca' ? 'PCA components' : 'PLS components';
        newErrors.parameters = `Number of ${paramName} (${nComponents}) cannot exceed number of data columns (${preview.dataColumns.length})`;
      }
    }

    // Check for PLS-DA component count
    if (analysisType === 'plsda') {
      const nComponents = config.parameters.n_components || 2;
      if (nComponents > preview.dataColumns.length) {
        newErrors.parameters = `Number of PLS-DA components (${nComponents}) cannot exceed number of data columns (${preview.dataColumns.length})`;
      }
    }

    // Check for SIMCA component count
    if (analysisType === 'simca') {
      const nComponents = config.parameters.n_components || 2;
      if (nComponents > preview.dataColumns.length) {
        newErrors.parameters = `Number of SIMCA components (${nComponents}) cannot exceed number of data columns (${preview.dataColumns.length})`;
      }
    }

    // Check feature selection parameters
    const fsParams = config.parameters.feature_selection;
    if (fsParams && fsParams.enabled && typeof fsParams.k === 'number') {
      if (fsParams.k > preview.dataColumns.length) {
        newErrors.feature_selection = `Number of features to select (${fsParams.k}) cannot exceed available data columns (${preview.dataColumns.length})`;
      }
      if (fsParams.k < 1) {
        newErrors.feature_selection = 'Number of features to select must be at least 1';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Number of samples (distinct sample_name) of every class in the data range:
  // { class label: number of samples }. Nested validation splits by samples.
  const getClassSampleCounts = () => {
    if (!tableData || !config.label_column || !config.data_range) return {};
    try {
      const samples = {};
      parseDataRanges(config.data_range).rows.forEach(rowIndex => {
        const row = tableData.data_matrix[rowIndex];
        const label = row?.[config.label_column];
        if (label === null || label === undefined || label === '') return;
        const key = label.toString();
        const sample = row.sample_name === null || row.sample_name === undefined || row.sample_name === ''
          ? 'unknown' : row.sample_name.toString();
        (samples[key] = samples[key] || new Set()).add(sample);
      });
      return Object.fromEntries(Object.entries(samples).map(([label, set]) => [label, set.size]));
    } catch {
      return {};
    }
  };

  // Calculate label statistics for validation.
  // Pure: it must not write component state (originalRange / includedLabels are initialised
  // explicitly by the reset effect and maintained by the filter handlers).
  const calculateLabelStats = (labelColumn, dataRange) => {
    console.log("calculateLabelStats: Starting");
    console.log("labelColumn:", labelColumn);
    console.log("dataRange:", dataRange);

    if (!labelColumn || !tableData) {
      console.log("calculateLabelStats: Missing labelColumn or tableData, returning null");
      return null;
    }

    try {
      let preview = null;
      let labelCounts = {};
      let uniqueLabels = new Set();
      let totalCount = 0;
      let hasMissing = false;
      let rangeCount = 0;
      let isMultiRange = false;

      // Try to parse the current data range if it exists
      if (dataRange && dataRange.trim() !== '') {
        try {
          preview = parseDataRanges(dataRange);
          console.log("Parsed preview from dataRange:", preview);

          preview.rows.forEach(rowIndex => {
            const label = getRowLabel(rowIndex, labelColumn);
            if (label !== '') {
              uniqueLabels.add(label);
              labelCounts[label] = (labelCounts[label] || 0) + 1;
            }
          });

          totalCount = preview.rows.length;
          hasMissing = preview.rows.some(rowIndex => {
            const row = tableData.data_matrix[rowIndex];
            return row[labelColumn] === null || row[labelColumn] === undefined || row[labelColumn] === '';
          });
          rangeCount = preview.rangeCount;
          isMultiRange = preview.isMultiRange;

        } catch (error) {
          console.error('Error parsing dataRange:', error);
          // Continue with empty stats
        }
      } else {
        console.log("dataRange is empty or invalid, using empty stats");
      }

      // Get original counts from the original range if it exists
      let originalCounts = {};
      if (originalRange && originalRange.trim() !== '') {
        console.log("Using originalRange for originalCounts:", originalRange);
        try {
          const originalPreview = parseDataRanges(originalRange);
          originalPreview.rows.forEach(rowIndex => {
            const label = getRowLabel(rowIndex, labelColumn);
            if (label !== '') {
              originalCounts[label] = (originalCounts[label] || 0) + 1;
            }
          });
        } catch (error) {
          console.error('Error parsing originalRange:', error);
          // Fallback to current counts as original if available
          if (Object.keys(labelCounts).length > 0) {
            originalCounts = { ...labelCounts };
          }
        }
      } else {
        // No originalRange yet, use current counts as original if available
        if (Object.keys(labelCounts).length > 0) {
          console.log("No originalRange, using current counts as original");
          originalCounts = { ...labelCounts };
        }
      }

      // If we have originalCounts but no current labelCounts (empty data range),
      // populate labelCounts with zeros for all original labels
      if (Object.keys(originalCounts).length > 0 && Object.keys(labelCounts).length === 0) {
        console.log("Data range is empty, populating labelCounts with zeros from originalCounts");
        Object.keys(originalCounts).forEach(label => {
          labelCounts[label] = 0;
        });
        // Also add these labels to uniqueLabels set for consistent counting
        Object.keys(originalCounts).forEach(label => {
          uniqueLabels.add(label);
        });
      }

      console.log("Unique labels found:", Array.from(uniqueLabels));
      console.log("Label counts (after zero-fill):", labelCounts);
      console.log("Original counts:", originalCounts);

      // Build result object
      const result = {
        uniqueCount: uniqueLabels.size > 0 ? uniqueLabels.size : Object.keys(originalCounts).length,
        totalCount: totalCount,
        labelCounts: labelCounts, // Now guaranteed to have entries if originalCounts exists
        originalCounts: originalCounts,
        hasMissing: hasMissing,
        rangeCount: rangeCount,
        isMultiRange: isMultiRange,
        // Add flags to indicate source
        hasCurrentData: Object.keys(labelCounts).length > 0 && Object.values(labelCounts).some(count => count > 0),
        hasOriginalData: Object.keys(originalCounts).length > 0,
        isEmptyRange: dataRange === '' || dataRange === null || dataRange === undefined
      };

      console.log("calculateLabelStats: Returning result:", result);
      return result;

    } catch (error) {
      console.error('Error calculating label stats:', error);
      console.error('Error stack:', error.stack);
      return null;
    }
  };

  const validateConfig = () => {
    const newErrors = {};

    // Basic validation
    if (!config.name.trim()) {
      newErrors.name = 'Analysis name is required';
    }

    if (!config.data_range.trim()) {
      const noColumns = includedColumns !== null && includedColumns.size === 0;
      const noLabels = !!config.label_column && !!labelStats?.hasOriginalData && includedLabels.size === 0;
      if (noColumns && noLabels) {
        newErrors.data_range = 'Select at least one feature column and at least one label';
      } else if (noColumns) {
        newErrors.data_range = 'Select at least one feature column';
      } else if (noLabels) {
        newErrors.data_range = 'Select at least one label';
      } else {
        newErrors.data_range = 'Data range is required';
      }
    } else if (config.data_range.length > 500) {
      newErrors.data_range = `Data range specification is too long (${config.data_range.length} characters, maximum 500). Deselect fewer scattered columns or simplify label filtering.`;
    } else {
      // Validate range format and content
      try {
        const preview = parseDataRanges(config.data_range);
        if (!preview.dataColumns || preview.dataColumns.length === 0) {
          newErrors.data_range = 'Selected range must contain at least one data column';
        }

        // Analysis-specific data requirements
        const classificationAlgorithms = [
          'lda', 'logistic', 'svm', 'random_forest', 'knn',
          'plsda', 'simca', 'pipeline', 'xgboost', 'bbc', 'brf'
        ];
        if (classificationAlgorithms.includes(analysisType)) {
          if (preview.dataColumns.length < 2) {
            newErrors.data_range = `${getAnalysisDisplayName(analysisType)} requires at least 2 data columns`;
          }
        }

        // Check for sufficient samples
        if (preview.rows < 2) {
          newErrors.data_range = 'Selected range must contain at least 2 rows';
        }

      } catch (error) {
        newErrors.data_range = 'Invalid range format. Use format like A1:Z100';
      }
    }

    if (!config.label_column) {
      newErrors.label_column = 'Label column is required';
    } else {
      // Validate label column
      console.log("Calc ls sample_name validate config "+config.data_range);
      const stats = calculateLabelStats(config.label_column, config.data_range);
      if (stats) {
        if (stats.hasMissing) {
          newErrors.label_column = 'Label column contains missing values';
        }

        if (stats.uniqueCount < 2) {
          newErrors.label_column = 'Label column must contain at least 2 unique values';
        }

        // Classification-specific label validation
        const classificationAlgorithms = [
          'lda', 'logistic', 'svm', 'random_forest', 'knn',
          'plsda', 'simca', 'pipeline', 'xgboost', 'bbc', 'brf'
        ];
        if (classificationAlgorithms.includes(analysisType)) {
          if (stats.uniqueCount < 2) {
            newErrors.label_column = `${getAnalysisDisplayName(analysisType)} requires at least 2 classes`;
          }

          // Check for class imbalance in classification
          const minCount = Math.min(...Object.values(stats.labelCounts));
          const maxCount = Math.max(...Object.values(stats.labelCounts));
          const imbalanceRatio = maxCount / minCount;

          // if (imbalanceRatio > 10) {
          //   newErrors.label_column = `Warning: Severe class imbalance detected (ratio: ${imbalanceRatio.toFixed(1)}:1). Consider using BBC or BRF methods.`;
          // }
        }

        // Cross-validation validation
        const supervisedMethods = [
          'lda', 'logistic', 'svm', 'random_forest', 'knn',
          'plsda', 'simca', 'pipeline', 'xgboost', 'bbc', 'brf'
        ];
        if (supervisedMethods.includes(analysisType) && config.cv_method !== 'leave_one_out') {
          if (stats.uniqueCount > config.cv_folds) {
            // This is acceptable, just a warning
            console.warn(`More classes (${stats.uniqueCount}) than CV folds (${config.cv_folds})`);
          }

          // Check if any class has fewer samples than CV folds for stratified CV
          if (config.cv_method === 'stratified') {
            const problematicClasses = Object.entries(stats.labelCounts)
              .filter(([_, count]) => count < config.cv_folds)
              .map(([label, _]) => label);

            if (problematicClasses.length > 0) {
              newErrors.cv_folds = `Stratified CV requires at least ${config.cv_folds} samples per class. Problematic classes: ${problematicClasses.join(', ')}`;
            }
          }
        }
      }
    }

    // Parameter validation
    if (analysisType === 'pca' || analysisType === 'pipeline') {
      const nComponents = analysisType === 'pca'
        ? config.parameters.n_components || 5
        : config.parameters.pls_n_components || 5;

      try {
        const preview = parseDataRanges(config.data_range);
        if (nComponents > preview.dataColumns.length) {
          const paramName = analysisType === 'pca' ? 'PCA components' : 'PLS components';
          newErrors.parameters = `Number of ${paramName} (${nComponents}) cannot exceed number of data columns (${preview.dataColumns.length})`;
        }
        if (nComponents < 1) {
          newErrors.parameters = `Number of ${analysisType === 'pca' ? 'PCA' : 'PLS'} components must be at least 1`;
        }
        if (nComponents > 50) {
          newErrors.parameters = `Number of ${analysisType === 'pca' ? 'PCA' : 'PLS'} components cannot exceed 50`;
        }
      } catch (error) {
        // Range parsing failed, error already captured above
      }
    }

    // PLS-DA parameter validation
    if (analysisType === 'plsda') {
      const nComponents = config.parameters.n_components || 2;
      try {
        const preview = parseDataRanges(config.data_range);
        if (nComponents > preview.dataColumns.length) {
          newErrors.parameters = `Number of PLS-DA components (${nComponents}) cannot exceed number of data columns (${preview.dataColumns.length})`;
        }
        if (nComponents < 1) {
          newErrors.parameters = 'Number of PLS-DA components must be at least 1';
        }
        if (nComponents > 20) {
          newErrors.parameters = 'Number of PLS-DA components cannot exceed 20';
        }
      } catch (error) {
        // Range parsing failed, error already captured above
      }
    }

    // SIMCA parameter validation
    if (analysisType === 'simca') {
      const nComponents = config.parameters.n_components || 2;
      try {
        const preview = parseDataRanges(config.data_range);
        if (nComponents > preview.dataColumns.length) {
          newErrors.parameters = `Number of SIMCA components (${nComponents}) cannot exceed number of data columns (${preview.dataColumns.length})`;
        }
        if (nComponents < 1) {
          newErrors.parameters = 'Number of SIMCA components must be at least 1';
        }
        if (nComponents > 20) {
          newErrors.parameters = 'Number of SIMCA components cannot exceed 20';
        }
      } catch (error) {
        // Range parsing failed, error already captured above
      }
    }

    // KNN parameter validation
    if (analysisType === 'knn') {
      const nNeighbors = config.parameters.n_neighbors || 5;
      if (nNeighbors < 1) {
        newErrors.parameters = 'Number of neighbors must be at least 1';
      }
      if (nNeighbors > 50) {
        newErrors.parameters = 'Number of neighbors cannot exceed 50';
      }
    }

    // XGBoost parameter validation
    if (analysisType === 'xgboost') {
      const params = config.parameters;
      if (params.learning_rate <= 0) {
        newErrors.parameters = 'Learning rate must be greater than 0';
      }
      if (params.max_depth && params.max_depth < 1) {
        newErrors.parameters = 'Max depth must be at least 1 or null';
      }
      if (params.n_estimators < 10) {
        newErrors.parameters = 'Number of estimators must be at least 10';
      }
      if (params.subsample <= 0 || params.subsample > 1) {
        newErrors.parameters = 'Subsample must be between 0 and 1';
      }
      if (params.colsample_bytree <= 0 || params.colsample_bytree > 1) {
        newErrors.parameters = 'Column sample by tree must be between 0 and 1';
      }
    }

    // Balanced Bagging Classifier parameter validation
    if (analysisType === 'bbc') {
      const params = config.parameters;
      if (params.n_estimators < 1) {
        newErrors.parameters = 'Number of estimators must be at least 1';
      }
      if (params.max_samples <= 0 || params.max_samples > 1) {
        newErrors.parameters = 'Max samples must be between 0 and 1';
      }
      if (params.max_features <= 0 || params.max_features > 1) {
        newErrors.parameters = 'Max features must be between 0 and 1';
      }
    }

    // Balanced Random Forest parameter validation
    if (analysisType === 'brf') {
      const params = config.parameters;
      if (params.n_estimators < 10) {
        newErrors.parameters = 'Number of estimators must be at least 10';
      }
      if (params.min_samples_split < 2) {
        newErrors.parameters = 'Min samples split must be at least 2';
      }
      if (params.min_samples_leaf < 1) {
        newErrors.parameters = 'Min samples leaf must be at least 1';
      }
    }

    // Feature selection validation
    const fsParams = config.parameters.feature_selection;
    if (fsParams && fsParams.enabled && typeof fsParams.k === 'number') {
      try {
        const preview = parseDataRanges(config.data_range);
        if (fsParams.k > preview.dataColumns.length) {
          newErrors.feature_selection = `Number of features to select (${fsParams.k}) cannot exceed available data columns (${preview.dataColumns.length})`;
        }
        if (fsParams.k < 1) {
          newErrors.feature_selection = 'Number of features to select must be at least 1';
        }
        if (fsParams.k > 100) {
          newErrors.feature_selection = 'Number of features to select cannot exceed 100';
        }
      } catch (error) {
        // Range parsing failed, error already captured above
      }
    }

    // Nested validation: range of the number of features and the number of samples per class
    if (isNestedMode && canUseNested) {
      if (fsParams && fsParams.enabled) {
        const kMin = fsParams.k_min ?? 1;
        const kMax = fsParams.k_max ?? null;
        let columnCount = null;
        try {
          columnCount = parseDataRanges(config.data_range).dataColumns.length;
        } catch (error) {
          // Range parsing failed, error already captured above
        }
        if (!Number.isInteger(kMin) || kMin < 1) {
          newErrors.feature_selection = 'Number of features to try: "from" must be at least 1';
        } else if (kMax !== null && (!Number.isInteger(kMax) || kMax < kMin)) {
          newErrors.feature_selection = 'Number of features to try: "to" must not be less than "from"';
        } else if (columnCount !== null && kMin > columnCount) {
          newErrors.feature_selection = `Number of features to try: "from" (${kMin}) cannot exceed available data columns (${columnCount})`;
        }
      }

      const sampleCounts = getClassSampleCounts();
      const single = Object.entries(sampleCounts).filter(([_, n]) => n < 2).map(([label]) => label);
      const small = Object.entries(sampleCounts).filter(([_, n]) => n < 3).map(([label]) => label);
      if (single.length > 0 && !config.is_classes_enumerating) {
        newErrors.validation = `Class(es) ${single.join(', ')} have a single sample: such a class cannot be validated. Add samples of the class or exclude it.`;
      } else if (small.length > 0 && !config.is_classes_enumerating) {
        newErrors.validation = `Nested validation needs at least 3 samples in every class (class(es) ${small.join(', ')} have fewer). Add samples, or use Simple validation.`;
      }
    }

    const supervisedMethods = [
      'lda', 'logistic', 'svm', 'random_forest', 'knn',
      'plsda', 'simca', 'pipeline', 'xgboost', 'bbc', 'brf'
    ];
    if (supervisedMethods.includes(analysisType) && config.cv_folds < 2 && !config.parameters.grid_search_enabled) {
      newErrors.cv_folds = 'Cross-validation folds must be at least 2';
    }

    if (supervisedMethods.includes(analysisType) && config.cv_folds > 200) {
      newErrors.cv_folds = 'Cross-validation folds cannot exceed 200';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // The config as it is sent to the server (also used for the estimate of the request)
  const buildSubmitConfig = () => {
    // Unchecked "Enable Feature Selection" keeps the previous k in state
    // (restored if re-checked) - never send it (pipeline/GridSearch left as is)
    const fs = config.parameters?.feature_selection;
    return (fs && fs.enabled === false && fs.k !== 'all' && analysisType !== 'pipeline')
      ? { ...config, parameters: { ...config.parameters, feature_selection: { ...fs, k: 'all' } } }
      : config;
  };

  const handleSubmit = () => {
    if (validateConfig()) {
      onSubmit(buildSubmitConfig());
    }
  };

  const handlePreview = () => {
    if (!tableData || !config.data_range) return;

    try {
      console.log("handle preview data range "+config.data_range);
      const preview = parseDataRanges(config.data_range);
      setPreviewData(preview);

      // Validate the data range
      validateDataRange(preview);

      // Calculate and display label statistics if label column is selected
      if (config.label_column) {
        console.log("Calc ls sample_name handle preview "+config.data_range);
        const stats = calculateLabelStats(config.label_column, config.data_range);
        setLabelStats(stats);
      }
    } catch (error) {
      setErrors({ ...errors, data_range: 'Invalid data range' });
    }
  };

  // Parse multiple ranges in Excel format
  const parseDataRanges = (rangesStr) => {
    if (!rangesStr.trim()) {
      throw new Error('Empty range string');
    }

    const rangeParts = rangesStr.split(',').map(part => part.trim());
    const allColumns = new Set();
    const allRows = new Set();
    const allDataColumns = new Set();

    for (const rangePart of rangeParts) {
      const rangeData = parseSingleRange(rangePart);

      // Merge columns, rows, and data columns from all ranges
      rangeData.columns.forEach(col => allColumns.add(col));
      rangeData.rows.forEach(row => allRows.add(row));
      rangeData.dataColumns.forEach(col => allDataColumns.add(col));
    }

    // Convert sets back to arrays and sort
    const columns = Array.from(allColumns).sort((a, b) => {
      const orderedColumns = getOrderedColumns();
      return orderedColumns.indexOf(a) - orderedColumns.indexOf(b);
    });

    const rows = Array.from(allRows).sort((a, b) => a - b);
    const dataColumns = Array.from(allDataColumns).sort((a, b) => {
      const orderedColumns = getOrderedColumns();
      return orderedColumns.indexOf(a) - orderedColumns.indexOf(b);
    });

    return {
      columns,
      rows,
      dataColumns,
      rangeCount: rangeParts.length,
      isMultiRange: rangeParts.length > 1
    };
  };

  // Parse a single range in Excel format
  const parseSingleRange = (range) => {
    const singleCellMatch = range.match(/^([A-Z]+)(\d+)$/);
    const rangeMatch = range.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/);

    let startCol, startRow, endCol, endRow;

    if (singleCellMatch) {
      // Single cell
      startCol = excelColumnToIndex(singleCellMatch[1]);
      startRow = parseInt(singleCellMatch[2]) - 1;
      endCol = startCol;
      endRow = startRow;
    } else if (rangeMatch) {
      // Range
      startCol = excelColumnToIndex(rangeMatch[1]);
      startRow = parseInt(rangeMatch[2]) - 1;
      endCol = excelColumnToIndex(rangeMatch[3]);
      endRow = parseInt(rangeMatch[4]) - 1;
    } else {
      throw new Error(`Invalid range format: ${range}. Use format like A1:Z100 or AA1:AC50`);
    }

    const orderedColumns = getOrderedColumns();

    // Validate column indices
    if (startCol < 0 || startCol >= orderedColumns.length ||
        endCol < 0 || endCol >= orderedColumns.length) {
      throw new Error(`Column range out of bounds: ${range}`);
    }

    // Validate row indices
    if (startRow < 0 || startRow >= tableData.data_matrix.length ||
        endRow < 0 || endRow >= tableData.data_matrix.length) {
      throw new Error(`Row range out of bounds: ${range}`);
    }

    const selectedColumns = orderedColumns.slice(startCol, endCol + 1);
    const selectedRows = Array.from({ length: endRow - startRow + 1 }, (_, i) => startRow + i);

    return {
      columns: selectedColumns,
      rows: selectedRows,
      dataColumns: selectedColumns.filter(col =>
        tableData.columns_metadata[col]?.type === 'data'
      ),
      startCol: indexToExcelColumn(startCol),
      endCol: indexToExcelColumn(endCol),
      startRow: startRow + 1,
      endRow: endRow + 1
    };
  };

  const getLabelColumnOptions = () => {
    if (!tableData) return [];

    return Object.entries(tableData.columns_metadata)
      .filter(([_, meta]) => meta.type === 'sample_info' || meta.type === 'class')
      .map(([name, meta]) => ({
        value: name,
        label: meta.type === 'class' ?
          `${meta.class_type || name} (Classification)` :
          `${name.replace('_', ' ')} (Sample Info)`
      }));
  };

  const renderParameterFields = () => {
    const params = config.parameters;

    if (analysisType === 'all') {
      return (
        <Alert variant="info" className="mb-0">
          <i className="bi bi-info-circle me-2"></i>
          All analysis types will be performed with their default parameters.
        </Alert>
      );
    }

    switch (analysisType) {
      case 'pca':
        return (
          <Form.Group>
            <Form.Label>Number of Components</Form.Label>
            <Form.Control
              type="number"
              min="1"
              max="50"
              value={params.n_components || 5}
              onChange={(e) => setConfig({
                ...config,
                parameters: { ...params, n_components: parseInt(e.target.value) }
              })}
              isInvalid={!!errors.parameters}
            />
            <Form.Control.Feedback type="invalid">
              {errors.parameters}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Number of principal components to compute (max: number of data columns)
            </Form.Text>
          </Form.Group>
        );

      case 'lda':
        return (
          <Form.Group>
            <Form.Label>Solver</Form.Label>
            <Form.Select
              value={params.solver || 'svd'}
              onChange={(e) => setConfig({
                ...config,
                parameters: { ...params, solver: e.target.value }
              })}
            >
              <option value="svd">SVD</option>
              <option value="eigen">Eigen</option>
            </Form.Select>
          </Form.Group>
        );

      case 'logistic':
        return (
          <>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Regularization (C)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    min="0.01"
                    value={params.C || 1.0}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, C: parseFloat(e.target.value) }
                    })}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Max Iterations</Form.Label>
                  <Form.Control
                    type="number"
                    min="100"
                    max="5000"
                    value={params.max_iter || 1000}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, max_iter: parseInt(e.target.value) }
                    })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        );

      case 'svm':
        return (
          <>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Kernel</Form.Label>
                  <Form.Select
                    value={params.kernel || 'rbf'}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, kernel: e.target.value }
                    })}
                  >
                    <option value="rbf">RBF</option>
                    <option value="linear">Linear</option>
                    <option value="poly">Polynomial</option>
                    <option value="sigmoid">Sigmoid</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Regularization (C)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    min="0.01"
                    value={params.C || 1.0}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, C: parseFloat(e.target.value) }
                    })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        );

      case 'random_forest':
        return (
          <>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Number of Trees</Form.Label>
                  <Form.Control
                    type="number"
                    min="10"
                    max="500"
                    value={params.n_estimators || 100}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, n_estimators: parseInt(e.target.value) }
                    })}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Max Depth</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="50"
                    placeholder="Auto"
                    value={params.max_depth || ''}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: {
                        ...params,
                        max_depth: e.target.value ? parseInt(e.target.value) : null
                      }
                    })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        );

      case 'knn':
        return (
          <>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Number of Neighbors</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="50"
                    value={params.n_neighbors || 5}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, n_neighbors: parseInt(e.target.value) }
                    })}
                    isInvalid={!!errors.parameters}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.parameters}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Weight Function</Form.Label>
                  <Form.Select
                    value={params.weights || 'uniform'}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, weights: e.target.value }
                    })}
                  >
                    <option value="uniform">Uniform</option>
                    <option value="distance">Distance</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <Form.Group>
                  <Form.Label>Distance Metric</Form.Label>
                  <Form.Select
                    value={params.metric || 'minkowski'}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, metric: e.target.value }
                    })}
                  >
                    <option value="minkowski">Minkowski</option>
                    <option value="euclidean">Euclidean</option>
                    <option value="manhattan">Manhattan</option>
                    <option value="chebyshev">Chebyshev</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    label="Scale Data"
                    checked={params.scale_data !== false}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, scale_data: e.target.checked }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Standardize features before applying KNN
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </>
        );

      case 'plsda':
        // Determine if search is enabled (truthy object)
        const searchEnabled = params.search_optimal_n_components !== false && params.search_optimal_n_components !== undefined;

        // Helper to update search params
        const updateSearchParam = (key, value) => {
          const currentSearch = params.search_optimal_n_components || {};
          setConfig({
            ...config,
            parameters: {
              ...params,
              search_optimal_n_components: { ...currentSearch, [key]: value }
            }
          });
        };

        return (
          <>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Number of Components</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="20"
                    value={params.n_components || 2}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, n_components: parseInt(e.target.value) }
                    })}
                    isInvalid={!!errors.parameters}
                    disabled={searchEnabled}  // Disabled when search is on
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.parameters}
                  </Form.Control.Feedback>
                  {searchEnabled && (
                    <Form.Text className="text-muted">
                      Will be determined automatically via search.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>
                    Classification Style
                    <HelpLink path={HARD_SOFT_PLSDA_DOC} title="Documentation: hard vs soft PLS-DA" />
                  </Form.Label>
                  <Form.Select
                    value={params.style || 'hard'}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, style: e.target.value }
                    })}
                  >
                    <option value="hard">Hard</option>
                    <option value="soft">Soft</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* New Row: Search toggle */}
            <Row className="mt-2">
              <Col>
                <Form.Group>
                  <Form.Check
                    type="switch"
                    label={
                      <>
                        Search optimal n_components
                        <HelpLink
                          path={`${METHOD_DOCS.plsda}#choosing-the-number-of-components`}
                          title="Documentation: choosing the number of components"
                        />
                      </>
                    }
                    checked={searchEnabled}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // Enable search with defaults
                        setConfig({
                          ...config,
                          parameters: {
                            ...params,
                            search_optimal_n_components: {
                              metric: 'accuracy',
                              min_n: 2,
                              max_n: 10
                            }
                          }
                        });
                      } else {
                        // Disable search
                        const { search_optimal_n_components, ...restParams } = params;
                        setConfig({
                          ...config,
                          parameters: restParams
                        });
                      }
                    }}
                  />
                  <Form.Text className="text-muted">
                    Automatically find the best number of components via cross‑validation.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Conditionally show search parameters */}
            {searchEnabled && (
              <>
                <Row className="mt-2">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Optimization Metric</Form.Label>
                      <Form.Select
                          value={params.search_optimal_n_components.metric || 'accuracy'}
                          onChange={(e) => updateSearchParam('metric', e.target.value)}
                      >
                        <option value="accuracy">Accuracy</option>
                        <option value="f1_macro">F1 Macro</option>
                        <option value="f1_weighted">F1 Weighted</option>
                        <option value="precision_macro">Precision Macro</option>
                        <option value="recall_macro">Recall Macro</option>
                        <option value="sensitivity">Sensitivity (binary)</option>
                        <option value="specificity">Specificity (binary)</option>
                        <option value="balanced_accuracy">Balanced Accuracy (binary)</option>
                        <option value="r2">R2 (binary)</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Min Components</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={params.search_optimal_n_components.min_n || 2}
                        onChange={(e) => updateSearchParam('min_n', parseInt(e.target.value) || 1)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Max Components</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={params.search_optimal_n_components.max_n || 10}
                        onChange={(e) => updateSearchParam('max_n', parseInt(e.target.value) || 1)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Text className="text-muted">
                  The search will try all integers from min to max (inclusive) and pick the best according to the metric.
                </Form.Text>
              </>
            )}

            <Row className="mt-2">
              <Col>
                <Form.Group>
                  <Form.Label>Significance Level (α)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="0.5"
                    value={params.alpha || 0.05}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, alpha: parseFloat(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Type I error rate for classification
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    label="Scale Features"
                    checked={params.scale_x !== false}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, scale_x: e.target.checked }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Standardize features before PLS‑DA
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </>
        );

      case 'simca':
        return (
          <>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Number of Components</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="20"
                    value={params.n_components || 2}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, n_components: parseInt(e.target.value) }
                    })}
                    isInvalid={!!errors.parameters}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.parameters}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Significance Level (α)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="0.5"
                    value={params.alpha || 0.05}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, alpha: parseFloat(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Type I error rate for class acceptance
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    label="Scale Features"
                    checked={params.scale_x !== false}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, scale_x: e.target.checked }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Standardize features before SIMCA
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </>
        );
      case 'xgboost':
        return (
          <>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Number of Trees</Form.Label>
                  <Form.Control
                    type="number"
                    min="10"
                    max="1000"
                    value={params.n_estimators || 100}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, n_estimators: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">Number of gradient boosted trees</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Learning Rate</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="1"
                    value={params.learning_rate || 0.1}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, learning_rate: parseFloat(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">Step size shrinkage to prevent overfitting</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Max Depth</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="20"
                    placeholder="6 (default)"
                    value={params.max_depth || ''}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: {
                        ...params,
                        max_depth: e.target.value ? parseInt(e.target.value) : 6
                      }
                    })}
                  />
                  <Form.Text className="text-muted">Maximum depth of a tree</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Min Child Weight</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="10"
                    value={params.min_child_weight || 1}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, min_child_weight: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">Minimum sum of instance weight needed in a child</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Subsample</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="1"
                    value={params.subsample || 1.0}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, subsample: parseFloat(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">Fraction of samples for each tree (prevents overfitting)</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Column Sample</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="1"
                    value={params.colsample_bytree || 1.0}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, colsample_bytree: parseFloat(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">Fraction of features for each tree</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Gamma</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={params.gamma || 0}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, gamma: parseFloat(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">Minimum loss reduction to make further partition</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Alpha (L1)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={params.reg_alpha || 0}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, reg_alpha: parseFloat(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">L1 regularization term on weights</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Lambda (L2)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={params.reg_lambda || 1}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, reg_lambda: parseFloat(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">L2 regularization term on weights</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Early Stopping Rounds</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="50"
                    value={params.early_stopping_rounds || 10}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, early_stopping_rounds: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">Stop if validation score doesn't improve for N rounds</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Evaluation Metric</Form.Label>
                  <Form.Select
                    value={params.eval_metric || 'mlogloss'}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, eval_metric: e.target.value }
                    })}
                  >
                    <option value="mlogloss">Multi-class Log Loss</option>
                    <option value="merror">Multi-class Error Rate</option>
                    <option value="auc">AUC</option>
                    <option value="logloss">Binary Log Loss</option>
                    <option value="error">Binary Error Rate</option>
                  </Form.Select>
                  <Form.Text className="text-muted">Metric used for early stopping</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Tree Method</Form.Label>
                  <Form.Select
                    value={params.tree_method || 'auto'}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, tree_method: e.target.value }
                    })}
                  >
                    <option value="auto">Auto</option>
                    <option value="exact">Exact</option>
                    <option value="approx">Approximate</option>
                    <option value="hist">Histogram</option>
                  </Form.Select>
                  <Form.Text className="text-muted">Tree construction algorithm</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Objective Function</Form.Label>
                  <Form.Select
                    value={params.objective || 'multi:softprob'}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, objective: e.target.value }
                    })}
                  >
                    <option value="multi:softprob">Multi-class (soft probabilities)</option>
                    <option value="multi:softmax">Multi-class (hard classification)</option>
                    <option value="binary:logistic">Binary Classification</option>
                  </Form.Select>
                  <Form.Text className="text-muted">Learning objective</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Booster</Form.Label>
                  <Form.Select
                    value={params.booster || 'gbtree'}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...params, booster: e.target.value }
                    })}
                  >
                    <option value="gbtree">Gradient Boosted Trees</option>
                    <option value="gblinear">Linear Booster</option>
                    <option value="dart">Dropout Additive Regression Trees</option>
                  </Form.Select>
                  <Form.Text className="text-muted">Type of boosting algorithm</Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </>
        );

    case 'bbc':
      return (
        <>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Number of Estimators</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="50"
                  value={params.n_estimators || 10}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, n_estimators: parseInt(e.target.value) }
                  })}
                />
                <Form.Text className="text-muted">Number of base estimators in the ensemble</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Max Samples</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="1.0"
                  value={params.max_samples || 1.0}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, max_samples: parseFloat(e.target.value) }
                  })}
                />
                <Form.Text className="text-muted">Minimum 0.5 to ensure all classes are represented</Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Sampling Strategy</Form.Label>
                <Form.Select
                  value={params.sampling_strategy || 'auto'}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, sampling_strategy: e.target.value }
                  })}
                >
                  <option value="auto">Auto (resample all classes)</option>
                  <option value="majority">Resample only majority class</option>
                  <option value="not minority">All classes except minority</option>
                  <option value="not majority">All classes except majority</option>
                  <option value="all">Resample all classes equally</option>
                </Form.Select>
                <Form.Text className="text-muted">Controls how classes are balanced</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Replacement</Form.Label>
                <Form.Select
                  value={params.replacement || false}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, replacement: e.target.value === 'true' }
                  })}
                >
                  <option value={false}>False (no replacement)</option>
                  <option value={true}>True (with replacement)</option>
                </Form.Select>
                <Form.Text className="text-muted">Sample with replacement to ensure class diversity</Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={6}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label="Bootstrap Samples"
                  checked={params.bootstrap !== false}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, bootstrap: e.target.checked }
                  })}
                />
                <Form.Text className="text-muted">Whether samples are drawn with replacement</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Random State</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="10000"
                  value={params.random_state || 42}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, random_state: parseInt(e.target.value) }
                  })}
                />
                <Form.Text className="text-muted">Seed for reproducible results</Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <hr className="mt-3 mb-3" />
          <Row>
            <Col>
              <h6>Decision Tree Parameters (Base Estimator)</h6>
              <Row className="mt-2">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Max Depth</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="20"
                      placeholder="None (default)"
                      value={params.base_estimator?.max_depth || ''}
                      onChange={(e) => setConfig({
                        ...config,
                        parameters: {
                          ...params,
                          base_estimator: {
                            ...params.base_estimator,
                            max_depth: e.target.value ? parseInt(e.target.value) : null
                          }
                        }
                      })}
                    />
                    <Form.Text className="text-muted">Maximum tree depth</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Min Samples Split</Form.Label>
                    <Form.Control
                      type="number"
                      min="2"
                      max="20"
                      value={params.base_estimator?.min_samples_split || 2}
                      onChange={(e) => setConfig({
                        ...config,
                        parameters: {
                          ...params,
                          base_estimator: {
                            ...params.base_estimator,
                            min_samples_split: parseInt(e.target.value)
                          }
                        }
                      })}
                    />
                    <Form.Text className="text-muted">Minimum samples to split a node</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Min Samples Leaf</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="10"
                      value={params.base_estimator?.min_samples_leaf || 1}
                      onChange={(e) => setConfig({
                        ...config,
                        parameters: {
                          ...params,
                          base_estimator: {
                            ...params.base_estimator,
                            min_samples_leaf: parseInt(e.target.value)
                          }
                        }
                      })}
                    />
                    <Form.Text className="text-muted">Minimum samples in a leaf</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>
          <Alert variant="info" className="mt-3">
            <i className="bi bi-info-circle me-2"></i>
            <strong>BBC (Balanced Bagging Classifier)</strong> is designed for imbalanced datasets.
            It combines bagging with undersampling to balance classes in each bootstrap sample.
            Use higher "Max Samples" values (≥0.8) and enable "Replacement" for better results with small classes.
          </Alert>
        </>
      );

    case 'brf':
      return (
        <>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Number of Trees</Form.Label>
                <Form.Control
                  type="number"
                  min="10"
                  max="500"
                  value={params.n_estimators || 100}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, n_estimators: parseInt(e.target.value) }
                  })}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Criterion</Form.Label>
                <Form.Select
                  value={params.criterion || 'gini'}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, criterion: e.target.value }
                  })}
                >
                  <option value="gini">Gini</option>
                  <option value="entropy">Entropy</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Max Depth</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="50"
                  placeholder="Auto"
                  value={params.max_depth || ''}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: {
                      ...params,
                      max_depth: e.target.value ? parseInt(e.target.value) : null
                    }
                  })}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Max Features</Form.Label>
                <Form.Select
                  value={params.max_features || 'sqrt'}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, max_features: e.target.value }
                  })}
                >
                  <option value="sqrt">√(features)</option>
                  <option value="log2">log2(features)</option>
                  <option value="auto">All features</option>
                  <option value="0.1">10%</option>
                  <option value="0.5">50%</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Min Samples Split</Form.Label>
                <Form.Control
                  type="number"
                  min="2"
                  max="20"
                  value={params.min_samples_split || 2}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, min_samples_split: parseInt(e.target.value) }
                  })}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Min Samples Leaf</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="10"
                  value={params.min_samples_leaf || 1}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, min_samples_leaf: parseInt(e.target.value) }
                  })}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Sampling Strategy</Form.Label>
                <Form.Select
                  value={params.sampling_strategy || 'auto'}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, sampling_strategy: e.target.value }
                  })}
                >
                  <option value="auto">Auto</option>
                  <option value="not minority">Not Minority</option>
                  <option value="not majority">Not Majority</option>
                  <option value="all">All</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Class Weight</Form.Label>
                <Form.Select
                  value={params.class_weight || 'balanced_subsample'}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, class_weight: e.target.value }
                  })}
                >
                  <option value="balanced_subsample">Balanced Subsample</option>
                  <option value="balanced">Balanced</option>
                  <option value="none">None</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={6}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label="Bootstrap"
                  checked={params.bootstrap !== false}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, bootstrap: e.target.checked }
                  })}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label="Replacement"
                  checked={params.replacement === true}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, replacement: e.target.checked }
                  })}
                />
                <Form.Text className="text-muted">Sample with replacement</Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </>
      );

      case 'pipeline':
        return renderPipelineParameters();

      default:
        return null;
    }
  };

  const renderPipelineParameters = () => {
    const params = config.parameters;

    // Get feature count from parsed data range
    const getFeatureCount = () => {
      if (!config.data_range) return 0;
      try {
        const preview = parseDataRanges(config.data_range);
        return preview.dataColumns.length;
      } catch (e) {
        return 0;
      }
    };

    const featureCount = getFeatureCount();

    // Helper to generate suggested k values based on feature count
    const getSuggestedKValues = () => {
      if (featureCount === 0) return [5, 10, 15, 20];

      const suggestions = [];
      // Small numbers for testing
      if (featureCount >= 5) suggestions.push(5);
      if (featureCount >= 10) suggestions.push(10);
      if (featureCount >= 15) suggestions.push(15);
      if (featureCount >= 20) suggestions.push(20);

      // Percentages of features
      const percentages = [0.1, 0.25, 0.5, 0.75];
      percentages.forEach(p => {
        const k = Math.round(featureCount * p);
        if (k >= 2 && k <= featureCount && !suggestions.includes(k)) {
          suggestions.push(k);
        }
      });

      // Add 'all' option if reasonable
      if (featureCount <= 100) {
        suggestions.push('all');
      }

      return [...new Set(suggestions)].sort((a, b) => {
        if (a === 'all') return 1;
        if (b === 'all') return -1;
        return a - b;
      });
    };

    // Helper to generate suggested PLS components based on k value
    const getSuggestedPLSComponents = (kValue) => {
      const maxComponents = kValue === 'all' ? Math.min(20, featureCount) : Math.min(20, kValue);
      const suggestions = [];

      // Always include small numbers
      if (maxComponents >= 2) suggestions.push(2);
      if (maxComponents >= 3) suggestions.push(3);
      if (maxComponents >= 5) suggestions.push(5);

      // Include more values for larger k
      if (maxComponents >= 8) suggestions.push(8);
      if (maxComponents >= 10) suggestions.push(10);
      if (maxComponents >= 15) suggestions.push(15);
      if (maxComponents >= 20) suggestions.push(20);

      // Include values up to max (but limit to reasonable number)
      const step = Math.max(1, Math.floor(maxComponents / 5));
      for (let i = step; i <= maxComponents; i += step) {
        if (!suggestions.includes(i)) suggestions.push(i);
      }

      return [...new Set(suggestions)].sort((a, b) => a - b);
    };

    // Helper to get grid param input value
    const getGridParamInputValue = (section, key) => {
      const gridKey = `${section}__${key}`;
      return gridParamInputValues[gridKey] || '';
    };

    // Helper to set grid param input value
    const setGridParamInputValue = (section, key, value) => {
      const gridKey = `${section}__${key}`;
      setGridParamInputValues(prev => ({
        ...prev,
        [gridKey]: value
      }));
    };

    // Helper to get dependent PLS input value
    const getDependentPLSInputValue = (kValue) => {
      return dependentPLSInputValues[kValue] || '';
    };

    // Helper to set dependent PLS input value
    const setDependentPLSInputValue = (kValue, value) => {
      setDependentPLSInputValues(prev => ({
        ...prev,
        [kValue]: value
      }));
    };

    // Add this function inside the renderPipelineParameters function, before toggleGridSearchForParam:

    const getDefaultValue = (section, key) => {
      // Get pipeline defaults
      const pipelineDefaults = getDefaultParameters('pipeline', config.data_range);

      // Get the current classifier type
      const classifierType = params.classifier || pipelineDefaults.classifier;

      // Get classifier-specific defaults
      const classifierDefaults = getDefaultParameters(classifierType, config.data_range);

      // Helper to get value from nested objects
      const getNestedValue = (obj, path) => {
        const keys = path.split('.');
        let current = obj;

        for (const key of keys) {
          if (current && typeof current === 'object' && key in current) {
            current = current[key];
          } else {
            return undefined;
          }
        }

        return current;
      };

      // Handle different sections
      if (section === 'feature_selection') {
        // Check if key exists in feature_selection defaults
        if (key in pipelineDefaults.feature_selection) {
          return pipelineDefaults.feature_selection[key];
        }

        // Check classifier defaults (some classifiers might have feature_selection params)
        if (classifierDefaults.feature_selection && key in classifierDefaults.feature_selection) {
          return classifierDefaults.feature_selection[key];
        }

        return null;

      } else if (section === 'pls') {
        if (key === 'n_components') {
          return pipelineDefaults.pls_n_components || 5;
        } else if (key === 'max_iter') {
          return pipelineDefaults.pls_max_iter || 500;
        }

        return null;

      } else if (section === 'classifier') {
        // First check classifier_params in pipeline defaults
        if (pipelineDefaults.classifier_params && key in pipelineDefaults.classifier_params) {
          return pipelineDefaults.classifier_params[key];
        }

        // Then check the main classifier defaults object
        if (key in classifierDefaults) {
          // Handle nested base_estimator parameters
          if (key === 'base_estimator' && typeof classifierDefaults.base_estimator === 'object') {
            return classifierDefaults.base_estimator;
          }
          return classifierDefaults[key];
        }

        // Check for nested classifier parameters (e.g., classifier_params.n_estimators)
        const nestedPath = `classifier_params.${key}`;
        const nestedValue = getNestedValue(pipelineDefaults, nestedPath);
        if (nestedValue !== undefined) {
          return nestedValue;
        }

        // Common classifier parameter fallbacks
        const commonDefaults = {
          // Random Forest
          n_estimators: 100,
          max_depth: null,
          min_samples_split: 2,
          min_samples_leaf: 1,
          class_weight: 'balanced',

          // XGBoost
          learning_rate: 0.1,
          subsample: 1.0,
          colsample_bytree: 1.0,
          scale_pos_weight: 1,

          // SVM
          C: 1.0,
          gamma: 'scale',
          kernel: 'rbf',

          // Logistic Regression
          penalty: 'l2',
          solver: 'lbfgs',
          max_iter: 1000,

          // KNN
          n_neighbors: 5,
          weights: 'uniform',
          p: 2,
          metric: 'minkowski',

          // Common parameters
          random_state: 42
        };

        if (key in commonDefaults) {
          return commonDefaults[key];
        }

        return null;
      }

      return null;
    };

    // Helper to update parameter - handles both fixed and grid search modes
    const updateParameter = (section, key, value) => {
      // If value is null or empty string and we're not in grid search mode,
      // use the default value from getDefaultParameters
      if ((value === null || value === '') && !params.grid_search_enabled) {
        const defaultParams = getDefaultParameters('pipeline', config.data_range);
        const classifierType = params.classifier || 'random_forest';
        const classifierDefaults = getDefaultParameters(classifierType, config.data_range);

        let defaultValue;
        if (section === 'feature_selection') {
          defaultValue = defaultParams.feature_selection?.[key];
        } else if (section === 'pls') {
          if (key === 'n_components') {
            defaultValue = defaultParams.pls_n_components;
          } else if (key === 'max_iter') {
            defaultValue = defaultParams.pls_max_iter;
          }
        } else if (section === 'classifier') {
          defaultValue = classifierDefaults[key] || defaultParams.classifier_params?.[key];
        }

        value = defaultValue;
      }

      if (params.grid_search_enabled) {
        const isOptimizing =
          (section === 'feature_selection' && gridSearchStages.feature_selection) ||
          (section === 'pls' && gridSearchStages.pls) ||
          (section === 'classifier' && gridSearchStages.classifier);

        if (isOptimizing) {
          const gridKey = section === 'feature_selection' ? `feature_selection__${key}` :
                         section === 'pls' ? `pls__${key}` :
                         `classifier__${key}`;

          // If value is a string, parse it as comma-separated values
          if (typeof value === 'string') {
            // Parse comma-separated string into array
            const parsedValues = value.split(',').map(v => {
              const trimmed = v.trim();
              if (trimmed === '') return null;
              if (trimmed === 'all') return 'all';
              if (trimmed === 'None' || trimmed === 'null') return null;
              if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10);
              if (/^\d*\.?\d+$/.test(trimmed)) return parseFloat(trimmed);
              return trimmed;
            }).filter(v => v !== null);

            const customGrid = params.custom_grid_params || {};
            customGrid[gridKey] = parsedValues.length > 0 ? parsedValues : null;

            // If updating feature selection k values, clean up stale dependent PLS components
            if (section === 'feature_selection' && key === 'k') {
              // Get current k values from the updated value
              const currentKValues = parsedValues;

              // Clean up any PLS component entries for k values that no longer exist
              Object.keys(customGrid).forEach(gridKey => {
                if (gridKey.startsWith('pls__n_components_')) {
                  const kValue = gridKey.replace('pls__n_components_', '');
                  // Parse kValue to match the format (could be number or 'all')
                  const parsedKValue = kValue === 'all' ? 'all' : parseInt(kValue, 10);

                  // Check if this k value still exists in the current k values
                  const kExists = currentKValues.some(k =>
                    k === parsedKValue ||
                    (typeof k === 'number' && k === parsedKValue) ||
                    (k === 'all' && parsedKValue === 'all')
                  );

                  if (!kExists) {
                    delete customGrid[gridKey];
                  }
                }
              });
            }

            setConfig({
              ...config,
              parameters: {
                ...params,
                custom_grid_params: customGrid
              }
            });
          } else if (Array.isArray(value) || value === null) {
            const customGrid = params.custom_grid_params || {};
            customGrid[gridKey] = value;

            // Clean up stale PLS components if updating feature selection k
            if (section === 'feature_selection' && key === 'k' && Array.isArray(value)) {
              Object.keys(customGrid).forEach(gridKey => {
                if (gridKey.startsWith('pls__n_components_')) {
                  const kValue = gridKey.replace('pls__n_components_', '');
                  const parsedKValue = kValue === 'all' ? 'all' : parseInt(kValue, 10);

                  const kExists = value.some(k =>
                    k === parsedKValue ||
                    (typeof k === 'number' && k === parsedKValue) ||
                    (k === 'all' && parsedKValue === 'all')
                  );

                  if (!kExists) {
                    delete customGrid[gridKey];
                  }
                }
              });
            }

            setConfig({
              ...config,
              parameters: {
                ...params,
                custom_grid_params: customGrid
              }
            });
          } else {
            // Single value - remove from grid search and set as fixed
            const customGrid = params.custom_grid_params || {};
            delete customGrid[gridKey];

            // Clean up stale PLS components if this was feature selection k
            if (section === 'feature_selection' && key === 'k') {
              Object.keys(customGrid).forEach(gridKey => {
                if (gridKey.startsWith('pls__n_components_')) {
                  delete customGrid[gridKey];
                }
              });
            }

            if (section === 'feature_selection') {
              setConfig({
                ...config,
                parameters: {
                  ...params,
                  custom_grid_params: customGrid,
                  feature_selection: {
                    ...params.feature_selection,
                    [key]: value
                  }
                }
              });
            } else if (section === 'pls') {
              setConfig({
                ...config,
                parameters: {
                  ...params,
                  custom_grid_params: customGrid,
                  [key === 'n_components' ? 'pls_n_components' : 'pls_max_iter']: value
                }
              });
            } else {
              setConfig({
                ...config,
                parameters: {
                  ...params,
                  custom_grid_params: customGrid,
                  classifier_params: {
                    ...params.classifier_params,
                    [key]: value
                  }
                }
              });
            }
          }
        } else {
          // Not optimizing this parameter - set as fixed
          if (section === 'feature_selection') {
            setConfig({
              ...config,
              parameters: {
                ...params,
                feature_selection: {
                  ...params.feature_selection,
                  [key]: value
                }
              }
            });
          } else if (section === 'pls') {
            setConfig({
              ...config,
              parameters: {
                ...params,
                [key === 'n_components' ? 'pls_n_components' : 'pls_max_iter']: value
              }
            });
          } else {
            setConfig({
              ...config,
              parameters: {
                ...params,
                classifier_params: {
                  ...params.classifier_params,
                  [key]: value
                }
              }
            });
          }
        }
      } else {
        // Not in grid search mode - set as fixed
        if (section === 'feature_selection') {
          setConfig({
            ...config,
            parameters: {
              ...params,
              feature_selection: {
                ...params.feature_selection,
                [key]: value
              }
            }
          });
        } else if (section === 'pls') {
          setConfig({
            ...config,
            parameters: {
              ...params,
              [key === 'n_components' ? 'pls_n_components' : 'pls_max_iter']: value
            }
          });
        } else {
          setConfig({
            ...config,
            parameters: {
              ...params,
              classifier_params: {
                ...params.classifier_params,
                [key]: value
              }
            }
          });
        }
      }
    };

    // Helper to toggle grid search for a specific parameter
    const toggleGridSearchForParam = (section, key, enable) => {
      const gridKey = section === 'feature_selection' ? `feature_selection__${key}` :
                     section === 'pls' ? `pls__${key}` :
                     `classifier__${key}`;

      const customGrid = params.custom_grid_params || {};

      if (enable) {
        let currentValue;
        if (section === 'feature_selection') {
          currentValue = params.feature_selection?.[key];
        } else if (section === 'pls') {
          currentValue = key === 'n_components' ? params.pls_n_components : params.pls_max_iter;
        } else {
          currentValue = params.classifier_params?.[key];
        }

        let initialArray;
        if (key === 'k') {
          initialArray = getSuggestedKValues().slice(0, 3);
        } else if (key === 'n_components') {
          initialArray = getSuggestedPLSComponents(params.feature_selection?.k || 10).slice(0, 3);
        } else {
          initialArray = [currentValue || getDefaultValue(section, key)];
        }

        customGrid[gridKey] = initialArray;
      } else {
        delete customGrid[gridKey];

        // If disabling feature selection k optimization, clean up dependent PLS components
        if (section === 'feature_selection' && key === 'k') {
          Object.keys(customGrid).forEach(gridKey => {
            if (gridKey.startsWith('pls__n_components_')) {
              delete customGrid[gridKey];
            }
          });
        }
      }

      setConfig({
        ...config,
        parameters: {
          ...params,
          custom_grid_params: customGrid
        }
      });
    };

    // Check if a parameter is being optimized in grid search
    const isParamOptimized = (section, key) => {
      if (!params.grid_search_enabled) return false;
      const gridKey = section === 'feature_selection' ? `feature_selection__${key}` :
                     section === 'pls' ? `pls__${key}` :
                     `classifier__${key}`;
      return params.custom_grid_params?.[gridKey] !== undefined;
    };

    // Get the grid search values for a parameter
    const getGridSearchValues = (section, key) => {
      if (!params.grid_search_enabled) return [];
      const gridKey = section === 'feature_selection' ? `feature_selection__${key}` :
                     section === 'pls' ? `pls__${key}` :
                     `classifier__${key}`;
      const values = params.custom_grid_params?.[gridKey];

      // Return empty array if null or undefined
      if (values == null) return [];

      // Ensure we return an array
      return Array.isArray(values) ? values : [values];
    };

    // Render parameter input - handles both fixed and grid search modes
    const renderParamInput = (section, key, label, type = 'number', options = {}) => {
      const isOptimized = isParamOptimized(section, key);
      const gridValues = getGridSearchValues(section, key);

      // Get the current input value from local state or from gridValues
      const currentInputValue = getGridParamInputValue(section, key) ||
        gridValues.map(v =>
          v === null ? 'None' :
          v === 'all' ? 'all' :
          String(v)
        ).join(', ');

      // Get current fixed value from params
      let fixedValue;
      if (section === 'feature_selection') {
        fixedValue = params.feature_selection?.[key];
      } else if (section === 'pls') {
        fixedValue = key === 'n_components' ? params.pls_n_components : params.pls_max_iter;
      } else {
        fixedValue = params.classifier_params?.[key];
      }

      // Get default value
      const defaultValue = getDefaultValue(section, key);

      // Display value - use fixedValue if set, otherwise show defaultValue as placeholder or computed value
      const displayValue = fixedValue !== undefined && fixedValue !== null && fixedValue !== ''
        ? fixedValue
        : defaultValue;

      // Parse comma-separated string to array
      const parseCommaSeparated = (str) => {
        if (!str.trim()) return null;

        return str.split(',').map(v => {
          const trimmed = v.trim();
          if (trimmed === '' || trimmed === 'None' || trimmed === 'null') return null;
          if (trimmed === 'all') return 'all';
          if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10);
          if (/^\d*\.?\d+$/.test(trimmed)) return parseFloat(trimmed);
          return trimmed;
        }).filter(v => v !== null);
      };

      return (
        <Form.Group className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Label className="mb-0">{label}</Form.Label>
            {params.grid_search_enabled && gridSearchStages[section] && (
              <Form.Check
                type="switch"
                id={`${section}-${key}-optimize`}
                label="Optimize"
                checked={isOptimized}
                onChange={(e) => {
                  toggleGridSearchForParam(section, key, e.target.checked);
                  // Clear local input value when toggling
                  setGridParamInputValue(section, key, '');
                }}
                className="small"
              />
            )}
          </div>

          {isOptimized && params.grid_search_enabled? (
            <div className="border rounded p-3 bg-light">
              <div className="d-flex align-items-center mb-2">
                <span className="me-2">Values:</span>
                <div className="flex-grow-1">
                  <Form.Control
                    type="text"
                    placeholder="Enter comma-separated values"
                    value={currentInputValue}
                    onChange={(e) => {
                      // Update local state only, not global state
                      setGridParamInputValue(section, key, e.target.value);
                    }}
                    onBlur={(e) => {
                      // Parse and update global state on blur
                      const parsedValues = parseCommaSeparated(e.target.value);
                      if (parsedValues) {
                        updateParameter(section, key, parsedValues);
                      } else {
                        updateParameter(section, key, null);
                      }
                      // Clear local input value after updating global state
                      setGridParamInputValue(section, key, '');
                    }}
                    onKeyDown={(e) => {
                      // Update on Enter key as well
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                  />
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="ms-2"
                  onClick={() => {
                    let suggestions = [];
                    if (key === 'k') {
                      suggestions = getSuggestedKValues();
                    } else if (key === 'n_components') {
                      suggestions = getSuggestedPLSComponents(params.feature_selection?.k || 10);
                    } else if (key === 'method') {
                      suggestions = ['anova', 'mutual_info'];
                    } else if (key === 'gamma') {
                      suggestions = ['scale', 'auto', 0.001, 0.01, 0.1, 1];
                    } else if (key === 'kernel') {
                      suggestions = ['rbf', 'linear'];
                    } else if (key === 'weights') {
                      suggestions = ['uniform', 'distance'];
                    } else if (key === 'penalty') {
                      suggestions = ['l2', 'None'];
                    } else if (key === 'solver') {
                      suggestions = ['lbfgs', 'newton-cg', 'saga'];
                    } else if (key === 'class_weight') {
                      suggestions = ['balanced', 'None'];
                    }

                    if (suggestions.length > 0) {
                      updateParameter(section, key, suggestions);
                      // Also update local input value for display
                      setGridParamInputValue(section, key, suggestions.join(', '));
                    }
                  }}
                  title="Select from suggestions"
                >
                  <i className="bi bi-list"></i>
                </Button>
              </div>
              <Form.Text className="text-muted">
                Enter comma-separated values to test in GridSearch
              </Form.Text>
            </div>
          ) : (
            <div>
              {type === 'select' ? (
                <Form.Select
                  value={displayValue || ''}
                  onChange={(e) => {
                    const newValue = e.target.value === '' ? null : e.target.value;
                    updateParameter(section, key, newValue);
                  }}
                >
                  <option value="">Select...</option>
                  {options.options?.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <>
                  <Form.Control
                    type={type}
                    value={fixedValue !== undefined && fixedValue !== null && fixedValue !== '' ? fixedValue : ''}
                    placeholder={defaultValue !== null && defaultValue !== undefined ? String(defaultValue) : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (type === 'number') {
                        updateParameter(section, key, val === '' ? null : parseInt(val));
                      } else {
                        updateParameter(section, key, val === '' ? null : val);
                      }
                    }}
                    {...options}
                  />
                  {fixedValue === undefined || fixedValue === null || fixedValue === '' ? (
                    <small className="text-muted">
                      Default: {defaultValue !== null && defaultValue !== undefined ? String(defaultValue) : 'Not set'}
                    </small>
                  ) : null}
                </>
              )}
              {options.helpText && (
                <Form.Text className="text-muted">
                  {options.helpText}
                </Form.Text>
              )}
            </div>
          )}
        </Form.Group>
      );
    };
    // Render dependent PLS components UI
    const renderDependentPLSComponents = () => {
      if (!params.grid_search_enabled) return null;
      const kValues = getGridSearchValues('feature_selection', 'k');

      if (!Array.isArray(kValues) || kValues.length === 0) {
        return (
          <Alert variant="warning">
            First define k values for feature selection to set dependent PLS components.
          </Alert>
        );
      }

      // Parse comma-separated string to array of numbers
      const parsePLSComponents = (str) => {
        if (!str.trim()) return [];
        return str.split(',').map(v => {
          const trimmed = v.trim();
          return trimmed === '' || isNaN(trimmed) ? null : parseInt(trimmed, 10);
        }).filter(v => v !== null);
      };

      return (
        <div className="border rounded p-3 bg-light">
          <h6 className="mb-3">
            <i className="bi bi-link me-2"></i>
            PLS Components Dependent on Feature Selection K
          </h6>

          {kValues.map((kValue, index) => {
            const gridKey = `pls__n_components_${kValue}`;
            const values = params.custom_grid_params?.[gridKey] || [];

            // Get the current input value from local state or from stored values
            const currentInputValue = getDependentPLSInputValue(kValue) ||
              (Array.isArray(values) ? values.join(', ') : '');

            return (
              <div key={index} className="mb-3 border-bottom pb-3">
                <div className="d-flex align-items-center mb-2">
                  <span className="fw-bold me-2">When k = {kValue}:</span>
                  <div className="flex-grow-1">
                    <Form.Control
                      type="text"
                      placeholder={`Enter PLS components for k=${kValue}`}
                      value={currentInputValue}
                      onChange={(e) => {
                        // Update local state only
                        setDependentPLSInputValue(kValue, e.target.value);
                      }}
                      onBlur={(e) => {
                        // Parse and update global state on blur
                        const parsedValues = parsePLSComponents(e.target.value);
                        const customGrid = params.custom_grid_params || {};
                        if (parsedValues.length > 0) {
                          customGrid[gridKey] = parsedValues;
                        } else {
                          delete customGrid[gridKey];
                        }
                        setConfig({
                          ...config,
                          parameters: {
                            ...params,
                            custom_grid_params: customGrid
                          }
                        });
                        // Clear local input value
                        setDependentPLSInputValue(kValue, '');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.currentTarget.blur();
                        }
                      }}
                    />
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="ms-2"
                    onClick={() => {
                      const suggestions = getSuggestedPLSComponents(kValue);
                      const selectedSuggestions = suggestions.slice(0, 4);
                      const customGrid = params.custom_grid_params || {};
                      customGrid[gridKey] = selectedSuggestions;
                      setConfig({
                        ...config,
                        parameters: {
                          ...params,
                          custom_grid_params: customGrid
                        }
                      });
                      // Update local input value for display
                      setDependentPLSInputValue(kValue, selectedSuggestions.join(', '));
                    }}
                    title="Select from suggestions"
                  >
                    <i className="bi bi-list"></i>
                  </Button>
                </div>
              </div>
            );
          })}

          <Form.Text className="text-muted">
            Define PLS components for each k value. GridSearch will test each combination.
          </Form.Text>
        </div>
      );
    };

    return (
      <div>
        {/* GridSearch toggle before tabs */}
        <div className="mb-3">
          <Form.Check
            type="switch"
            id="gridsearch-switch"
            label={
              <span className="fw-bold">
                <i className="bi bi-search me-2"></i>
                Enable GridSearch Hyperparameter Optimization
              </span>
            }
            checked={params.grid_search_enabled || false}
            onChange={(e) => {
              const newValue = e.target.checked;
              const updatedParams = { ...params, grid_search_enabled: newValue };

              if (!newValue) {
                // When disabling GridSearch:
                // 1. Reset stage selection
                setGridSearchStages({
                  feature_selection: false,
                  pls: false,
                  classifier: false
                });

                // 2. Reset dependent mapping
                setDependentParamMapping({});

                // 3. Clear local input states
                setGridParamInputValues({});
                setDependentPLSInputValues({});

                // 4. Remove custom grid params
                updatedParams.custom_grid_params = undefined;

                // 5. Remove grid_search_params if you want to reset those too
                // updatedParams.grid_search_params = getDefaultParameters('pipeline').grid_search_params;
              } else {
                // When enabling GridSearch, initialize with empty custom grid
                updatedParams.custom_grid_params = {};
              }

              setConfig({
                ...config,
                parameters: updatedParams
              });
            }}
          />
          <Form.Text className="text-muted">
            When enabled, use nested cross-validation to find optimal hyperparameters.
            When disabled, use fixed parameter values.
          </Form.Text>
        </div>

        {featureCount > 0 && (
          <Alert variant="info" className="mb-3">
            <i className="bi bi-info-circle me-2"></i>
            Dataset contains <strong>{featureCount}</strong> features. Use this as reference when setting parameters.
          </Alert>
        )}

        <Tabs
          activeKey={activeKey}
          onSelect={setActiveKey}
          className="mb-3"
        >
          {/* GridSearch Optimization Tab (only if GridSearch enabled) */}
          {params.grid_search_enabled && (
            <Tab eventKey="0" title="GridSearch Optimization">
              <div className="p-3">
                <Alert variant="info" className="mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  Define which pipeline stages to optimize. Use the checkboxes below to include stages in GridSearch.
                </Alert>

                <Row className="mb-3">
                  <Col>
                    <Form.Group>
                      <Form.Label>Scoring Metric</Form.Label>
                      <Form.Select
                        value={params.grid_search_params?.scoring || 'f1_weighted'}
                        onChange={(e) => setConfig({
                          ...config,
                          parameters: {
                            ...params,
                            grid_search_params: {
                              ...params.grid_search_params,
                              scoring: e.target.value
                            }
                          }
                        })}
                      >
                        <option value="accuracy">Accuracy</option>
                        <option value="f1_weighted">F1 Score (Weighted)</option>
                        <option value="f1_macro">F1 Score (Macro)</option>
                        <option value="precision">Precision</option>
                        <option value="recall">Recall</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>Inner CV Method</Form.Label>
                      <Form.Select
                        value={params.grid_search_params?.inner_cv_method || 'group'}
                        onChange={(e) => setConfig({
                          ...config,
                          parameters: {
                            ...params,
                            grid_search_params: {
                              ...params.grid_search_params,
                              inner_cv_method: e.target.value
                            }
                          }
                        })}
                      >
                        <option value="stratified">Stratified K-Fold</option>
                        <option value="kfold">Random K-Fold</option>
                        <option value="group">Group K-Fold</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col>
                    <Form.Group>
                      <Form.Label>Inner CV Folds</Form.Label>
                      <Form.Control
                        type="number"
                        min="2"
                        max="20"
                        value={params.grid_search_params?.inner_cv_folds || 5}
                        onChange={(e) => setConfig({
                          ...config,
                          parameters: {
                            ...params,
                            grid_search_params: {
                              ...params.grid_search_params,
                              inner_cv_folds: parseInt(e.target.value)
                            }
                          }
                        })}
                      />
                      <Form.Text className="text-muted">
                        Number of folds for inner cross-validation (GridSearch)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Stage selection for GridSearch */}
                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0">Stages to Optimize</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <Form.Check
                          type="checkbox"
                          id="gridsearch-fs"
                          label="Feature Selection"
                          checked={gridSearchStages.feature_selection}
                          onChange={() => toggleGridSearchStage('feature_selection')}
                        />
                      </div>
                      <div className="col-md-4">
                        <Form.Check
                          type="checkbox"
                          id="gridsearch-pls"
                          label="PLS"
                          checked={gridSearchStages.pls}
                          onChange={() => toggleGridSearchStage('pls')}
                        />
                      </div>
                      <div className="col-md-4">
                        <Form.Check
                          type="checkbox"
                          id="gridsearch-classifier"
                          label="Classifier"
                          checked={gridSearchStages.classifier}
                          onChange={() => toggleGridSearchStage('classifier')}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Alert variant="warning" className="mt-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <strong>Note:</strong> This uses nested cross-validation: outer CV for evaluation, inner CV for optimization.
                  Parameter ranges are defined in the respective tabs below.
                </Alert>
              </div>
            </Tab>
          )}

          {/* Feature Selection Tab */}
          <Tab eventKey="1" title="Feature Selection">
            <div className="p-3">
              <Form.Check
                type="checkbox"
                label="Enable Feature Selection"
                checked={params.feature_selection?.enabled || false}
                onChange={(e) => setConfig({
                  ...config,
                  parameters: {
                    ...params,
                    feature_selection: {
                      ...params.feature_selection,
                      enabled: e.target.checked
                    }
                  }
                })}
              />
              <Form.Text className="text-muted">
                Select most informative features before analysis to improve performance and interpretability
              </Form.Text>

              {params.feature_selection?.enabled && (
                <>
                  {/* Method selection - always fixed */}
                  <Form.Group className="mt-3">
                    <Form.Label>Selection Method</Form.Label>
                    <Form.Select
                      value={params.feature_selection?.method || 'anova'}
                      onChange={(e) => setConfig({
                        ...config,
                        parameters: {
                          ...params,
                          feature_selection: {
                            ...params.feature_selection,
                            method: e.target.value
                          }
                        }
                      })}
                    >
                      <option value="anova">ANOVA F-test</option>
                      <option value="mutual_info">Mutual Information</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {params.feature_selection?.method === 'anova'
                        ? 'ANOVA F-test: Select features based on variance between classes'
                        : 'Mutual Information: Select features based on dependency with target'
                      }
                    </Form.Text>
                  </Form.Group>

                  {/* Number of features (k) - can be fixed or optimized */}
                  {renderParamInput('feature_selection', 'k', 'Number of Features (k)', 'number', {
                    min: 1,
                    max: featureCount || 100,
                    placeholder: featureCount ? `1 to ${featureCount} or "all"` : 'Enter value or "all"',
                    helpText: `Number of top features to select. Max available: ${featureCount}. Use "all" for all features.`
                  })}

                  {/* Score threshold - always fixed */}
                  <Form.Group className="mt-3">
                    <Form.Label>Score Threshold</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={params.feature_selection?.threshold || 0}
                      onChange={(e) => setConfig({
                        ...config,
                        parameters: {
                          ...params,
                          feature_selection: {
                            ...params.feature_selection,
                            threshold: parseFloat(e.target.value)
                          }
                        }
                      })}
                    />
                    <Form.Text className="text-muted">
                      Minimum feature score threshold (0 = no threshold)
                    </Form.Text>
                  </Form.Group>
                </>
              )}
            </div>
          </Tab>

          {/* PLS Tab */}
          <Tab eventKey="2" title="PLS Configuration">
            <div className="p-3">
              {/* PLS components - can be fixed or optimized */}
              {renderParamInput('pls', 'n_components', 'Number of PLS Components', 'number', {
                min: 1,
                max: featureCount || 50,
                placeholder: featureCount ? `1 to ${Math.min(20, featureCount)}` : '1 to 20',
                helpText: `Number of PLS latent variables. Max recommended: ${Math.min(20, featureCount)}`
              })}

              {/* Max iterations - always fixed */}
              <Form.Group className="mb-3">
                <Form.Label>Max PLS Iterations</Form.Label>
                <Form.Control
                  type="number"
                  min="100"
                  max="5000"
                  value={params.pls_max_iter || 500}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, pls_max_iter: parseInt(e.target.value) }
                  })}
                />
                <Form.Text className="text-muted">
                  Maximum iterations for PLS convergence
                </Form.Text>
              </Form.Group>

              {/* Dependent PLS components UI if both feature selection and PLS are being optimized */}
              {params.grid_search_enabled &&
               gridSearchStages.feature_selection &&
               gridSearchStages.pls && (
                <div className="mt-4">
                  <Form.Check
                    type="switch"
                    id="dependent-pls"
                    label="Make PLS components dependent on feature selection k"
                    checked={dependentParamMapping.enabled || false}
                    onChange={(e) => {
                      setDependentParamMapping({
                        ...dependentParamMapping,
                        enabled: e.target.checked
                      });
                    }}
                    className="mb-3"
                  />

                  {dependentParamMapping.enabled && params.grid_search_enabled && renderDependentPLSComponents()}
                </div>
              )}

              <Alert variant="info" className="mt-3">
                <i className="bi bi-info-circle me-2"></i>
                PLS (Partial Least Squares) is used for dimensionality reduction before classification.
                This helps to focus on the most relevant variation in the data.
              </Alert>
            </div>
          </Tab>

          {/* Classifier Tab */}
          <Tab eventKey="3" title="Classifier">
            <div className="p-3">
              <Form.Group className="mb-3">
                <Form.Label>Classifier Type</Form.Label>
                <Form.Select
                  value={params.classifier || 'random_forest'}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...params, classifier: e.target.value }
                  })}
                >
                  <option value="random_forest">Random Forest</option>
                  <option value="xgboost">XGBoost (Gradient Boosting)</option>
                  <option value="svm">Support Vector Machine</option>
                  <option value="logistic">Logistic Regression</option>
                  <option value="knn">K-Nearest Neighbors</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  The classifier used after PLS dimensionality reduction. XGBoost is recommended for better performance.
                </Form.Text>
              </Form.Group>

              {/* Classifier-specific parameters */}
              {params.classifier === 'random_forest' && (
                <>
                  {renderParamInput('classifier', 'n_estimators', 'Number of Trees', 'number', {
                    min: 10,
                    max: 500,
                    helpText: 'Number of trees in the forest'
                  })}

                  {renderParamInput('classifier', 'max_depth', 'Max Depth', 'number', {
                    min: 1,
                    max: 50,
                    placeholder: 'None (unlimited)',
                    helpText: 'Maximum depth of the tree. Leave empty for unlimited.'
                  })}

                  {renderParamInput('classifier', 'min_samples_split', 'Min Samples Split', 'number', {
                    min: 2,
                    max: 20,
                    helpText: 'Minimum number of samples required to split an internal node'
                  })}

                  {renderParamInput('classifier', 'min_samples_leaf', 'Min Samples Leaf', 'number', {
                    min: 1,
                    max: 20,
                    helpText: 'Minimum number of samples required to be at a leaf node'
                  })}

                  {params.imbalance_handling?.enabled && (
                    renderParamInput('classifier', 'class_weight', 'Class Weight', 'select', {
                      options: [
                        { value: 'balanced', label: 'Balanced' },
                        { value: 'None', label: 'None' }
                      ],
                      helpText: 'Weights associated with classes for imbalance handling'
                    })
                  )}
                </>
              )}

              {params.classifier === 'xgboost' && (
                <>
                  {renderParamInput('classifier', 'n_estimators', 'Number of Estimators', 'number', {
                    min: 10,
                    max: 500,
                    helpText: 'Number of gradient boosted trees'
                  })}

                  {renderParamInput('classifier', 'learning_rate', 'Learning Rate', 'number', {
                    step: '0.01',
                    min: '0.01',
                    max: '1',
                    helpText: 'Boosting learning rate (eta)'
                  })}

                  {renderParamInput('classifier', 'max_depth', 'Max Depth', 'number', {
                    min: 1,
                    max: 20,
                    helpText: 'Maximum tree depth for base learners'
                  })}

                  {renderParamInput('classifier', 'subsample', 'Subsample Ratio', 'number', {
                    step: '0.1',
                    min: '0.1',
                    max: '1',
                    helpText: 'Subsample ratio of the training instance'
                  })}

                  {renderParamInput('classifier', 'colsample_bytree', 'Column Sample by Tree', 'number', {
                    step: '0.1',
                    min: '0.1',
                    max: '1',
                    helpText: 'Subsample ratio of columns when constructing each tree'
                  })}

                  {params.imbalance_handling?.enabled && labelStats?.uniqueCount === 2 && (
                    renderParamInput('classifier', 'scale_pos_weight', 'Scale Positive Weight', 'number', {
                      step: '0.1',
                      min: '0.1',
                      max: '10',
                      helpText: 'Balancing of positive and negative weights (for binary classification)'
                    })
                  )}
                </>
              )}

              {params.classifier === 'svm' && (
                <>
                  {renderParamInput('classifier', 'C', 'Regularization (C)', 'number', {
                    step: '0.1',
                    min: '0.01',
                    max: '100',
                    helpText: 'Regularization parameter'
                  })}

                  {renderParamInput('classifier', 'gamma', 'Gamma', 'select', {
                    options: [
                      { value: 'scale', label: 'Scale (1/(n_features * X.var()))' },
                      { value: 'auto', label: 'Auto (1/n_features)' },
                      { value: '0.001', label: '0.001' },
                      { value: '0.01', label: '0.01' },
                      { value: '0.1', label: '0.1' },
                      { value: '1', label: '1' }
                    ],
                    helpText: 'Kernel coefficient for rbf, poly and sigmoid'
                  })}

                  {renderParamInput('classifier', 'kernel', 'Kernel', 'select', {
                    options: [
                      { value: 'rbf', label: 'RBF' },
                      { value: 'linear', label: 'Linear' }
                    ],
                    helpText: 'Specifies the kernel type to be used in the algorithm'
                  })}

                  {params.imbalance_handling?.enabled && (
                    renderParamInput('classifier', 'class_weight', 'Class Weight', 'select', {
                      options: [
                        { value: 'balanced', label: 'Balanced' },
                        { value: 'None', label: 'None' }
                      ],
                      helpText: 'Weights associated with classes for imbalance handling'
                    })
                  )}
                </>
              )}

              {params.classifier === 'logistic' && (
                <>
                  {renderParamInput('classifier', 'C', 'Regularization (C)', 'number', {
                    step: '0.1',
                    min: '0.01',
                    max: '100',
                    helpText: 'Inverse of regularization strength'
                  })}

                  {renderParamInput('classifier', 'penalty', 'Penalty', 'select', {
                    options: [
                      { value: 'l2', label: 'L2' },
                      { value: 'None', label: 'None' }
                    ],
                    helpText: 'Norm used in the penalization'
                  })}

                  {renderParamInput('classifier', 'solver', 'Solver', 'select', {
                    options: [
                      { value: 'lbfgs', label: 'LBFGS' },
                      { value: 'newton-cg', label: 'Newton-CG' },
                      { value: 'saga', label: 'SAGA' }
                    ],
                    helpText: 'Algorithm to use in the optimization problem'
                  })}

                  {renderParamInput('classifier', 'max_iter', 'Max Iterations', 'number', {
                    min: 100,
                    max: 5000,
                    helpText: 'Maximum number of iterations taken for the solvers to converge'
                  })}

                  {params.imbalance_handling?.enabled && (
                    renderParamInput('classifier', 'class_weight', 'Class Weight', 'select', {
                      options: [
                        { value: 'balanced', label: 'Balanced' },
                        { value: 'None', label: 'None' }
                      ],
                      helpText: 'Weights associated with classes for imbalance handling'
                    })
                  )}
                </>
              )}

              {params.classifier === 'knn' && (
                <>
                  {renderParamInput('classifier', 'n_neighbors', 'Number of Neighbors', 'number', {
                    min: 1,
                    max: 50,
                    helpText: 'Number of neighbors to use'
                  })}

                  {renderParamInput('classifier', 'weights', 'Weight Function', 'select', {
                    options: [
                      { value: 'uniform', label: 'Uniform' },
                      { value: 'distance', label: 'Distance' }
                    ],
                    helpText: 'Weight function used in prediction'
                  })}

                  {renderParamInput('classifier', 'p', 'Power Parameter', 'number', {
                    min: 1,
                    max: 2,
                    helpText: 'Power parameter for Minkowski metric (1: Manhattan, 2: Euclidean)'
                  })}
                </>
              )}
            </div>
          </Tab>

          {/* Imbalance Handling Tab (unchanged) */}
          <Tab eventKey="4" title="Imbalance Handling">
            {/* ... Keep the existing imbalance handling UI ... */}
            <div className="p-3">
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Enable Imbalance Handling"
                  checked={params.imbalance_handling?.enabled || false}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: {
                      ...params,
                      imbalance_handling: {
                        ...params.imbalance_handling,
                        enabled: e.target.checked
                      }
                    }
                  })}
                />
                <Form.Text className="text-muted">
                  Enable special handling for imbalanced datasets (different class sizes)
                </Form.Text>
              </Form.Group>

              {params.imbalance_handling?.enabled && (
                <>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group>
                        <Form.Label>Handling Method</Form.Label>
                        <Form.Select
                          value={params.imbalance_handling?.method || 'class_weight'}
                          onChange={(e) => setConfig({
                            ...config,
                            parameters: {
                              ...params,
                              imbalance_handling: {
                                ...params.imbalance_handling,
                                method: e.target.value
                              }
                            }
                          })}
                        >
                          <option value="class_weight">Class Weight Adjustment</option>
                          <option value="custom_weights">Custom Class Weights</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Method for handling class imbalance
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  {params.imbalance_handling?.method === 'custom_weights' && (
                    <Alert variant="info" className="mb-3">
                      <i className="bi bi-info-circle me-2"></i>
                      Enter custom class weights as a JSON object. Example: {"{'class1': 1.0, 'class2': 2.0}"}
                    </Alert>
                  )}

                  {params.imbalance_handling?.method === 'custom_weights' && (
                    <Form.Group className="mb-3">
                      <Form.Label>Custom Class Weights (JSON)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder='{"Control": 1.0, "Disease": 2.0}'
                        value={params.imbalance_handling?.class_weights
                          ? JSON.stringify(params.imbalance_handling.class_weights, null, 2)
                          : ''}
                        onChange={(e) => {
                          try {
                            const weights = e.target.value ? JSON.parse(e.target.value) : null;
                            setConfig({
                              ...config,
                              parameters: {
                                ...params,
                                imbalance_handling: {
                                  ...params.imbalance_handling,
                                  class_weights: weights
                                }
                              }
                            });
                          } catch (error) {
                            // Keep invalid JSON for user to fix
                            setConfig({
                              ...config,
                              parameters: {
                                ...params,
                                imbalance_handling: {
                                  ...params.imbalance_handling,
                                  class_weights: e.target.value
                                }
                              }
                            });
                          }
                        }}
                        isInvalid={params.imbalance_handling?.class_weights &&
                          typeof params.imbalance_handling.class_weights === 'string'}
                      />
                      {params.imbalance_handling?.class_weights &&
                       typeof params.imbalance_handling.class_weights === 'string' && (
                        <Form.Control.Feedback type="invalid">
                          Invalid JSON format
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  )}

                  <Alert variant="warning" className="mt-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <strong>Supported classifiers for imbalance handling:</strong>
                    <ul className="mb-0 mt-2">
                      <li><strong>Random Forest</strong>: Class weight adjustment</li>
                      <li><strong>XGBoost</strong>: scale_pos_weight (binary) or sample_weight (multi-class)</li>
                      <li><strong>SVM</strong>: Class weight adjustment</li>
                      <li><strong>Logistic Regression</strong>: Class weight adjustment</li>
                      <li><strong>KNN</strong>: No built-in imbalance handling</li>
                    </ul>
                  </Alert>
                </>
              )}
            </div>
          </Tab>

          {/* Overview Tab (updated with better summary) */}
          <Tab eventKey="5" title="Overview">
            <div className="p-3">
              <Alert variant="info">
                <h6><i className="bi bi-diagram-3 me-2"></i>Pipeline Structure</h6>
                <p>Your analysis will use the following pipeline:</p>
                <ol className="mb-0">
                  <li>
                    <strong>Feature Selection</strong>: {params.feature_selection?.enabled
                      ? `${params.feature_selection.method}`
                      : 'Disabled'}
                  </li>
                  <li><strong>StandardScaler</strong>: Feature standardization</li>
                  <li><strong>PLSRegression</strong>: Dimensionality reduction</li>
                  <li>
                    <strong>Classifier</strong>: {params.classifier === 'xgboost' ? 'XGBoost' :
                      params.classifier === 'svm' ? 'SVM' :
                      params.classifier === 'logistic' ? 'Logistic' :
                      params.classifier === 'knn' ? 'KNN' : 'Random Forest'}
                  </li>
                </ol>
              </Alert>

              {/* Parameter Summary */}
              <div className="card mt-3">
                <div className="card-header">
                  <h6 className="mb-0">Parameter Configuration</h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4">
                      <h6>Feature Selection</h6>
                      {params.feature_selection?.enabled ? (
                        <div className="small">
                          <div><strong>Method:</strong> {params.feature_selection.method}</div>
                          <div><strong>k:</strong> {
                            isParamOptimized('feature_selection', 'k')
                              ? `GridSearch: ${getGridSearchValues('feature_selection', 'k').join(', ')}`
                              : params.feature_selection.k || 'all'
                          }</div>
                          <div><strong>Threshold:</strong> {params.feature_selection.threshold || 0}</div>
                        </div>
                      ) : (
                        <div className="small text-muted">Disabled</div>
                      )}
                    </div>

                    <div className="col-md-4">
                      <h6>PLS</h6>
                      <div className="small">
                        <div><strong>Components:</strong> {
                          isParamOptimized('pls', 'n_components')
                            ? `GridSearch: ${getGridSearchValues('pls', 'n_components').join(', ')}`
                            : params.pls_n_components || 5
                        }</div>
                        <div><strong>Max Iterations:</strong> {params.pls_max_iter || 500}</div>
                        {dependentParamMapping.enabled && (
                          <div className="text-info">
                            <small><i className="bi bi-link"></i> Dependent on k values</small>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <h6>Classifier</h6>
                      <div className="small">
                        <div><strong>Type:</strong> {params.classifier || 'random_forest'}</div>
                        {Object.entries(params.classifier_params || {}).map(([key, value]) => (
                          <div key={key}>
                            <strong>{key}:</strong> {
                              isParamOptimized('classifier', key)
                                ? `GridSearch: ${getGridSearchValues('classifier', key).join(', ')}`
                                : value
                            }
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {params.imbalance_handling?.enabled && (
                <Alert variant="warning" className="mt-3">
                  <h6><i className="bi bi-scale me-2"></i>Imbalance Handling</h6>
                  <p className="mb-1">Enabled with method: <strong>{params.imbalance_handling.method}</strong></p>
                  {params.imbalance_handling.method === 'custom_weights' &&
                   params.imbalance_handling.class_weights && (
                    <p className="mb-0">
                      Custom weights: {JSON.stringify(params.imbalance_handling.class_weights)}
                    </p>
                  )}
                </Alert>
              )}

              {params.grid_search_enabled && (
                <Alert variant="success" className="mt-3">
                  <h6><i className="bi bi-search me-2"></i>GridSearch Optimization</h6>
                  <p className="mb-1">Enabled with nested cross-validation:</p>
                  <ul className="mb-0">
                    <li><strong>Scoring:</strong> {params.grid_search_params?.scoring || 'f1_weighted'}</li>
                    <li><strong>Inner CV:</strong> {params.grid_search_params?.inner_cv_method || 'stratified'} with {params.grid_search_params?.inner_cv_folds || 5} folds</li>
                    <li><strong>Outer CV:</strong> {config.cv_method} with {config.cv_folds} folds</li>
                  </ul>
                  <p className="mt-2 mb-0">
                    <strong>Stages being optimized:</strong>
                    <ul className="mb-0">
                      {gridSearchStages.feature_selection && <li>Feature Selection</li>}
                      {gridSearchStages.pls && <li>PLS</li>}
                      {gridSearchStages.classifier && <li>Classifier</li>}
                      {!gridSearchStages.feature_selection && !gridSearchStages.pls && !gridSearchStages.classifier &&
                        <li><i>No stages selected for optimization</i></li>}
                    </ul>
                  </p>
                </Alert>
              )}

              {/* Pipeline visualization */}
              <div className="pipeline-visualization mt-3 p-2 border rounded bg-light">
                <h6 className="mb-2">Pipeline Flow:</h6>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-center">
                    <div className="badge bg-primary p-2">Input Data</div>
                    <div className="small">↓</div>
                  </div>
                  <div className="text-center">
                    <div className={`badge p-2 ${gridSearchStages.feature_selection ? 'bg-warning' : 'bg-info'}`}>
                      {gridSearchStages.feature_selection && <i className="bi bi-search me-1"></i>}
                      Feature Selection
                    </div>
                    <div className="small">↓</div>
                  </div>
                  <div className="text-center">
                    <div className="badge bg-warning p-2">StandardScaler</div>
                    <div className="small">↓</div>
                  </div>
                  <div className="text-center">
                    <div className={`badge p-2 ${gridSearchStages.pls ? 'bg-warning' : 'bg-success'}`}>
                      {gridSearchStages.pls && <i className="bi bi-search me-1"></i>}
                      PLS
                    </div>
                    <div className="small">↓</div>
                  </div>
                  <div className="text-center">
                    <div className={`badge p-2 ${gridSearchStages.classifier ? 'bg-warning' : 'bg-danger'}`}>
                      {gridSearchStages.classifier && <i className="bi bi-search me-1"></i>}
                      {params.classifier === 'xgboost' ? 'XGBoost' :
                       params.classifier === 'svm' ? 'SVM' :
                       params.classifier === 'logistic' ? 'Logistic' :
                       params.classifier === 'knn' ? 'KNN' : 'Random Forest'}
                    </div>
                    <div className="small">↓</div>
                  </div>
                  <div className="text-center">
                    <div className="badge bg-dark p-2">Predictions</div>
                  </div>
                </div>
                <div className="mt-2 text-center">
                  <small className="text-muted">
                    <i className="bi bi-search text-warning"></i> = GridSearch optimization
                  </small>
                </div>
              </div>
            </div>
          </Tab>
        </Tabs>
      </div>
    );
  };

  const renderPreprocessingFields = () => {
  const fsParams = config.parameters.feature_selection || {};
  const avgReplicates = config.parameters.average_replicates || false;

  // Algorithms that cannot handle a single sample per class
  const algosRequiringMultipleSamples = ['lda', ];
    const isAlgorithmRestricted = algosRequiringMultipleSamples.includes(
      analysisType.toLowerCase()
    );
    const isLabelSampleName = config.label_column === 'sample_name';
    const disableAveraging = isAlgorithmRestricted && isLabelSampleName;

    return (
      <>
        {/* ----- AVERAGING TECHNICAL REPLICATES ----- */}
        <Row className="mb-3">
          <Col>
            <Form.Check
              type="checkbox"
              label={
                <>
                  Average technical replicates
                  <HelpLink path={REPLICATES_DOC} title="Documentation: averaging of replicates" />
                </>
              }
              checked={disableAveraging ? false : avgReplicates}
              disabled={disableAveraging}
              onChange={(e) =>
                setConfig({
                  ...config,
                  parameters: {
                    ...config.parameters,
                    average_replicates: e.target.checked,
                  },
                })
              }
            />
            <Form.Text className="text-muted">
              Group samples by the same <code>sample_name</code> and average their
              values before analysis.
            </Form.Text>
            {disableAveraging && (
              <Form.Text className="text-warning" style={{ display: 'block' }}>
                ⚠️ Averaging is disabled because the label column is set to{' '}
                <code>sample_name</code> and the selected algorithm (
                <strong>{analysisType.toUpperCase()}</strong>) requires at least two samples
                per class. Please change the label column or choose a different
                algorithm.
              </Form.Text>
            )}
          </Col>
        </Row>

        <hr /> {/* optional visual separator */}

        {/* ----- FEATURE SELECTION (existing UI) ----- */}
        <Row className="mb-3">
          <Col>
            <Form.Check
              type="checkbox"
              label={
                <>
                  Enable Feature Selection
                  <HelpLink path={FEATURE_SELECTION_DOC} title="Documentation: feature selection" />
                </>
              }
              checked={fsParams.enabled || false}
              onChange={(e) =>
                setConfig({
                  ...config,
                  parameters: {
                    ...config.parameters,
                    feature_selection: {
                      ...fsParams,
                      enabled: e.target.checked,
                    },
                  },
                })
              }
            />
            <Form.Text className="text-muted">
              Select most informative features before analysis to improve
              performance and interpretability
            </Form.Text>
          </Col>
        </Row>

        {fsParams.enabled && (
          <>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>
                    Selection Method
                    <HelpLink
                      path={`${FEATURE_SELECTION_DOC}#methods`}
                      title="Documentation: feature selection methods"
                    />
                  </Form.Label>
                  <Form.Select
                    value={fsParams.method || 'anova'}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        parameters: {
                          ...config.parameters,
                          feature_selection: {
                            ...fsParams,
                            method: e.target.value,
                          },
                        },
                      })
                    }
                  >
                    <option value="anova">ANOVA F-test</option>
                    <option value="mutual_info">Mutual Information</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {fsParams.method === 'anova'
                      ? 'ANOVA F-test: Select features based on variance between classes'
                      : 'Mutual Information: Select features based on dependency with target'}
                  </Form.Text>
                </Form.Group>
              </Col>
              {isNestedMode && canUseNested ? (
              <Col>
                <Form.Group>
                  <Form.Label>
                    Number of Features to Try
                    <HelpLink
                      path={`${NESTED_DOC}#settings`}
                      title="Documentation: nested validation settings"
                    />
                  </Form.Label>
                  <InputGroup hasValidation>
                    <InputGroup.Text>from</InputGroup.Text>
                    <Form.Control
                      type="number"
                      min="1"
                      placeholder="1"
                      value={fsParams.k_min ?? 1}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          parameters: {
                            ...config.parameters,
                            feature_selection: {
                              ...fsParams,
                              k_min: e.target.value ? parseInt(e.target.value) : 1,
                            },
                          },
                        })
                      }
                      isInvalid={!!errors.feature_selection}
                    />
                    <InputGroup.Text>to</InputGroup.Text>
                    <Form.Control
                      type="number"
                      min="1"
                      placeholder="All"
                      value={fsParams.k_max ?? ''}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          parameters: {
                            ...config.parameters,
                            feature_selection: {
                              ...fsParams,
                              k_max: e.target.value ? parseInt(e.target.value) : null,
                            },
                          },
                        })
                      }
                      isInvalid={!!errors.feature_selection}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.feature_selection}
                    </Form.Control.Feedback>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Nested validation: the number of features (k) is selected automatically
                    inside every fold. Leave "to" empty to try up to all features.
                    {estimate?.k_grid?.length > 0 && (
                      <> Every value of the range is tried: {estimate.k_grid.length > 1
                        ? `${estimate.k_grid[0]} – ${estimate.k_grid[estimate.k_grid.length - 1]} (${estimate.k_grid.length} values)`
                        : estimate.k_grid[0]}.</>
                    )}
                  </Form.Text>
                </Form.Group>
              </Col>
              ) : (
              <Col>
                <Form.Group>
                  <Form.Label>
                    Number of Features
                    <HelpLink
                      path={`${FEATURE_SELECTION_DOC}#number-of-features-k`}
                      title="Documentation: number of features (k)"
                    />
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="100"
                    placeholder="All"
                    value={(fsParams.k === 'all' || fsParams.k === 'auto') ? '' : fsParams.k}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        parameters: {
                          ...config.parameters,
                          feature_selection: {
                            ...fsParams,
                            k: e.target.value ? parseInt(e.target.value) : 'all',
                          },
                        },
                      })
                    }
                    isInvalid={!!errors.feature_selection}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.feature_selection}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Number of top features to select. Leave empty to use all
                    features.
                  </Form.Text>
                </Form.Group>
              </Col>
              )}
            </Row>
            {!isNestedMode && canUseNested && (
              <Alert variant="warning" className="mt-3 mb-0 d-flex justify-content-between align-items-center">
                <span>
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Feature selection is on - Nested validation is recommended: with Simple validation
                  the features are chosen using the samples the model is then tested on, and the
                  cross-validation metrics are overestimated.
                  <HelpLink
                    path={`${FEATURE_SELECTION_DOC}#feature-selection-inside-cross-validation`}
                    title="Documentation: feature selection inside cross-validation"
                  />
                </span>
                <Button
                  variant="outline-dark"
                  size="sm"
                  className="ms-3 text-nowrap"
                  onClick={() => setValidationParams({ mode: 'nested' })}
                >
                  Use nested
                </Button>
              </Alert>
            )}
          </>
        )}
      </>
    );
  };

  const getOrderedColumns = () => {
    if (!tableData.columns_metadata) return [];

    const sampleCols = [];
    const classCols = [];
    const dataCols = [];

    Object.entries(tableData.columns_metadata).forEach(([colName, metadata]) => {
      if (metadata.type === 'sample_info') {
        sampleCols.push(colName);
      } else if (metadata.type === 'class') {
        classCols.push(colName);
      } else if (metadata.type === 'data') {
        dataCols.push(colName);
      }
    });

    // Sort data columns by job chronology using job_order field
    dataCols.sort((a, b) => {
      const metaA = tableData.columns_metadata[a];
      const metaB = tableData.columns_metadata[b];

      // Use job_order if available, otherwise fall back to job_id
      const orderA = metaA.job_order !== undefined ? metaA.job_order : metaA.job_id;
      const orderB = metaB.job_order !== undefined ? metaB.job_order : metaB.job_id;

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      // Sort channels: R, G, B, then intensities - same order as DataTable and backend
      // parse_data_range (explicit undefined check: 'R' has order 0, which is falsy)
      const channelOrder = { 'R': 0, 'G': 1, 'B': 2, 'intensity': 3, 'rg_intensity': 4, 'rb_intensity': 5, 'gb_intensity': 6 };
      const channelA = channelOrder[metaA.channel] !== undefined ? channelOrder[metaA.channel] : 99;
      const channelB = channelOrder[metaB.channel] !== undefined ? channelOrder[metaB.channel] : 99;
      return channelA - channelB;
    });

    return [...sampleCols, ...classCols, ...dataCols];
  };

  // Full list of feature (data) columns available for the column filter checkboxes.
  // Anchored to originalRange, same as the label filter's originalCounts, so the
  // checkbox list stays stable regardless of the current row/column selection.
  // NOTE: must stay below parseDataRanges/parseSingleRange/getOrderedColumns
  // (defined above) since this memo calls them synchronously on first render.
  const allDataColumns = React.useMemo(() => {
    const base = originalRange || config.data_range;
    if (!base) return [];
    try {
      return parseDataRanges(base).dataColumns;
    } catch {
      return [];
    }
  }, [originalRange, tableData]);

  if (!tableData) return null;

  // The estimate line is not shown for one quick simple analysis
  const showEstimate = !!estimate && Array.isArray(estimate.wall_seconds) && (
    estimate.allowed === false || estimate.n_analyses > 1 || estimate.n_nested > 0 ||
    estimate.cpu_seconds >= 5 || (estimate.warnings || []).length > 0
  );

  // Nested validation with averaged replicates and 3 samples in a class: the inner training
  // parts have 2-3 rows of the class - LDA, k-NN and SIMCA may fail to fit there
  let nestedSmallSampleWarning = null;
  if (isNestedMode && canUseNested && config.parameters?.average_replicates &&
      ['lda', 'knn', 'simca', 'all'].includes(analysisType)) {
    const tight = Object.entries(getClassSampleCounts()).filter(([_, n]) => n === 3).map(([label]) => label);
    if (tight.length > 0) {
      nestedSmallSampleWarning = `Class(es) ${tight.join(', ')} have 3 samples: with averaged replicates ` +
        'LDA, k-NN and SIMCA may fail to fit in the inner loop. Add samples, do not average replicates, or use Simple validation.';
    }
  }

  // Docs page behind the "?" of the title: the method, or the batch run for "All Types"
  // ('linear' and 'pipeline' have no page)
  const methodDoc = METHOD_DOCS[analysisType];
  let dialogDoc = null;
  if (analysisType === 'all') {
    dialogDoc = {
      path: `${ANALYSIS_DIALOG_DOC}#running-several-methods-at-once`,
      title: 'Documentation: running several methods at once',
    };
  } else if (methodDoc) {
    dialogDoc = { path: methodDoc, title: `Documentation: ${getAnalysisDisplayName(analysisType)}` };
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-gear me-2"></i>
          Configure {getAnalysisDisplayName(analysisType)} Analysis
          {dialogDoc && (
            <HelpLink path={dialogDoc.path} title={dialogDoc.title} className="ms-2" />
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Basic Configuration */}
          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Analysis Name</Form.Label>
                <Form.Control
                  type="text"
                  value={config.name}
                  onChange={(e) => setConfig({ ...config, name: e.target.value })}
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* Data Selection */}
          <Accordion className="mb-3" defaultActiveKey="0">
            <Accordion.Item eventKey="0">
              <Accordion.Header>Data Selection</Accordion.Header>
              <Accordion.Body>
                <Row className="mb-3">
                  <Col>
                    <Form.Group>
                      <Form.Label>
                        Data Range
                        <Badge bg="info" className="ms-2">Excel Format</Badge>
                        <HelpLink
                          path={`${ANALYSIS_DIALOG_DOC}#data-range-and-label-column`}
                          title="Documentation: data range and label column"
                        />
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          placeholder="A1:Z100"
                          value={config.data_range}
                          onChange={(e) => {
                            adoptExternalRange(e.target.value); // typed by the user, not derived from the filters
                            setConfig(prev => ({ ...prev, data_range: e.target.value }));
                          }}
                          isInvalid={!!errors.data_range}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={handleRangeSelectionFromTable}
                          title="Select range from table"
                        >
                          <i className="bi bi-cursor"></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          onClick={handlePreview}
                        >
                          Preview
                        </Button>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid">
                        {errors.data_range}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Specify the range of cells containing feature data (e.g., D2:O98).
                        Click the cursor icon to select range interactively.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Form.Group>
                      <Form.Label>
                        Label Column
                        <HelpLink
                          path={`${ANALYSIS_DIALOG_DOC}#data-range-and-label-column`}
                          title="Documentation: data range and label column"
                        />
                      </Form.Label>
                      <Form.Select
                        value={config.label_column}
                        onChange={(e) => handleLabelColumnChange(e.target.value)}
                        isInvalid={!!errors.label_column}
                      >
                        <option value="">Select label column...</option>
                        {getLabelColumnOptions().map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.label_column}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Column containing sample names or class labels for analysis
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                {/* Column (Feature) Filtering Section */}
                {allDataColumns.length > 0 && (
                  <Row className="mb-3">
                    <Col>
                      <Form.Group>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center">
                          <Button
                            variant="link"
                            className="p-0 text-decoration-none text-dark d-flex align-items-center"
                            onClick={() => setIsColumnFilterExpanded(prev => !prev)}
                            aria-expanded={isColumnFilterExpanded}
                          >
                            <i className={`bi bi-chevron-${isColumnFilterExpanded ? 'down' : 'right'} me-2`}></i>
                            <Form.Label className="mb-0" as="span">
                              <span>
                                Feature Columns
                                <Badge bg="info" className="ms-2">
                                  {allDataColumns.length} total
                                </Badge>
                                <Badge bg={areAllColumnsIncluded() ? "success" : (includedColumns.size === 0 ? "danger" : "warning")} className="ms-2">
                                  {areAllColumnsIncluded() ? allDataColumns.length : includedColumns.size} selected
                                </Badge>
                              </span>
                            </Form.Label>
                          </Button>
                          <HelpLink
                            path={`${ANALYSIS_DIALOG_DOC}#selecting-feature-columns`}
                            title="Documentation: selecting feature columns"
                            className="ms-2"
                          />
                          </div>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleToggleAllColumns}
                          >
                            {areAllColumnsIncluded() ? 'Deselect All' : 'Select All'}
                          </Button>
                        </div>

                        {includedColumns !== null && includedColumns.size === 0 && (
                          <Form.Text className="text-danger d-block mb-2">
                            No feature columns selected - select at least one column
                          </Form.Text>
                        )}

                        <Collapse in={isColumnFilterExpanded}>
                          <div>
                            <div className="label-filter-container p-3 border rounded bg-light">
                              <Form.Text className="text-muted d-block mb-2">
                                Uncheck columns to exclude them from the analysis
                              </Form.Text>
                              <div className="row">
                                {allDataColumns.map(col => (
                                  <div key={col} className="col-6 col-md-4 col-lg-3 mb-2">
                                    <div className="d-flex align-items-center">
                                      <Form.Check
                                        type="checkbox"
                                        id={`col-${col}`}
                                        checked={includedColumns === null || includedColumns.has(col)}
                                        onChange={() => handleColumnToggle(col)}
                                        className="me-2"
                                      />
                                      <Form.Label htmlFor={`col-${col}`} className="mb-0 flex-grow-1">
                                        <span className="text-truncate d-block" title={col}>
                                          {col}
                                        </span>
                                      </Form.Label>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </Collapse>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                {/* Label Filtering Section */}
                {config.label_column && labelStats && (
                  <Row className="mb-3">
                    <Col>
                      <Form.Group>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          {/* Left side - Filter label and badges */}
                          <Form.Label className="mb-0">
                            <span>
                              Filter by {config.label_column} Labels
                              <Badge bg="info" className="ms-2">
                                {config.parameters?.total_classes?.length} total
                              </Badge>
                              <Badge bg={includedLabels.size === config.parameters?.total_classes?.length ? "success" : (includedLabels.size === 0 ? "danger" : "warning")} className="ms-2">
                                {includedLabels.size} selected
                              </Badge>
                            </span>
                          </Form.Label>

                          {/* Right side - Buttons and checkbox */}
                          <div className="d-flex align-items-center gap-2">
                            <div className="d-flex align-items-center">
                              <Form.Check
                                type="checkbox"
                                id="is-classes-enumerating"
                                checked={config.is_classes_enumerating}
                                onChange={(e) => setConfig({...config, is_classes_enumerating: e.target.checked})}
                                className="me-1"
                              />
                              <Form.Label className="mb-0" htmlFor="is-classes-enumerating">
                                All Classes Sets
                              </Form.Label>
                              <HelpLink
                                path={`${ANALYSIS_DIALOG_DOC}#running-several-methods-at-once`}
                                title="Documentation: All Classes Sets"
                              />
                            </div>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={handleToggleAllLabels}
                              disabled={config.is_classes_enumerating}
                            >
                              {areAllLabelsIncluded() ? 'Deselect All' : 'Select All'}
                            </Button>
                          </div>
                        </div>

                        {includedLabels.size === 0 && (
                          <Form.Text className="text-danger d-block mb-2">
                            No labels selected - select at least one label
                          </Form.Text>
                        )}

                        <div className="label-filter-container p-3 border rounded bg-light">
                          <Form.Text className="text-muted d-block mb-2">
                            Uncheck labels to exclude their rows from analysis
                          </Form.Text>

                          <div className="row">
                            {Object.entries(labelStats.originalCounts)
                              .sort(([a, _a], [b, _b]) => a.localeCompare(b))
                              .map(([label, count]) => (
                                <div key={label} className="col-6 col-md-4 col-lg-3 mb-2">
                                  <div className="d-flex align-items-center">
                                    <Form.Check
                                      type="checkbox"
                                      id={`label-${label}`}
                                      checked={includedLabels.has(label)}
                                      onChange={() => handleLabelToggle(label)}
                                      className="me-2"
                                      disabled={config.is_classes_enumerating}
                                    />
                                    <Form.Label htmlFor={`label-${label}`} className="mb-0 flex-grow-1">
                                      <span className="text-truncate d-block" title={label}>
                                        {label}
                                      </span>
                                    </Form.Label>
                                    <Badge bg={count > 0 ? "primary" : "secondary"} pill>
                                      {count}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                {/* Label Statistics */}
                {labelStats && (
                  <div className="stats-container">
                    <Alert variant={labelStats.hasMissing || labelStats.uniqueCount < 2 ? 'danger' : 'info'} className="mt-3">
                      <strong>Label Statistics:</strong><br/>
                      <div className="mb-2">
                        <Badge bg="primary" className="me-2">
                          {labelStats.uniqueCount} unique labels
                        </Badge>
                        <Badge bg="secondary" className="me-2">
                          {labelStats.totalCount} total samples
                        </Badge>
                        {labelStats.hasMissing && (
                          <Badge bg="danger" className="me-2">
                            Missing values detected
                          </Badge>
                        )}
                      </div>
                      <div className="stats-text">
                        Label distribution: {Object.entries(labelStats.labelCounts)
                          .map(([label, count]) => `${label}: ${count}`)
                          .join(', ')}
                      </div>
                    </Alert>
                  </div>
                )}

                {/* Preview */}
                {previewData && (
                  <div className="preview-container">
                    <Alert variant="info" className="mt-3">
                      <strong>Data Preview:</strong><br/>
                      <div className="mb-2">
                        {previewData.isMultiRange && (
                          <Badge bg="info" className="me-2">
                            {previewData.rangeCount} range{previewData.rangeCount > 1 ? 's' : ''}
                          </Badge>
                        )}
                        <Badge bg="secondary" className="me-2">
                          {previewData.rows.length} rows × {previewData.columns.length} columns
                        </Badge>
                        <Badge bg="success">
                          {previewData.dataColumns.length} data channels
                        </Badge>
                      </div>

                      <div className="preview-text">
                        <strong>Data columns:</strong> {previewData.dataColumns.slice(0, 5).join(', ')}
                        {previewData.dataColumns.length > 5 && ` ... (+${previewData.dataColumns.length - 5} more)`}
                      </div>

                      {previewData.rows.length > 0 && (
                        <div className="preview-text mt-1">
                          <strong>Row range:</strong> {Math.min(...previewData.rows) + 1} to {Math.max(...previewData.rows) + 1}
                          {previewData.isMultiRange && ' (across multiple ranges)'}
                        </div>
                      )}

                      {previewData.columns.length > 0 && (
                        <div className="preview-text mt-1">
                          <strong>Column span:</strong> {previewData.columns.length} column{previewData.columns.length > 1 ? 's' : ''}
                          {previewData.isMultiRange && ' from multiple ranges'}
                        </div>
                      )}

                      {previewData.isMultiRange && (
                        <div className="preview-text mt-1">
                          <small className="text-warning">
                            <i className="bi bi-exclamation-triangle"></i> Multiple ranges selected - data may be non-contiguous
                          </small>
                        </div>
                      )}
                    </Alert>
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Feature Selection */}
          <Accordion className="mb-3">
            <Accordion.Item eventKey="1">
              <Accordion.Header>Preprocessing</Accordion.Header>
              <Accordion.Body>
                {renderPreprocessingFields()}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Algorithm Parameters */}
          {analysisType !== 'all' && (
            <Accordion className="mb-3">
              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  {analysisType === 'pipeline' ? 'Pipeline Configuration' : 'Algorithm Parameters'}
                </Accordion.Header>
                <Accordion.Body>
                  {methodDoc && (
                    <div className="small text-muted mb-2">
                      {getAnalysisDisplayName(analysisType)}: parameters and their defaults
                      <HelpLink
                        path={`${methodDoc}#parameters`}
                        title={`Documentation: ${getAnalysisDisplayName(analysisType)} parameters`}
                      />
                    </div>
                  )}
                  {renderParameterFields()}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          )}

          {/* Cross-Validation */}
          {analysisType !== 'pca' && (
            <Accordion className="mb-3">
              <Accordion.Item eventKey="3">
                <Accordion.Header>Cross-Validation</Accordion.Header>
                <Accordion.Body>
                  {supportsNested && (
                    <Row className="mb-3">
                      <Col>
                        <Form.Group>
                          <Form.Label>
                            Validation
                            <HelpLink
                              path={`${VALIDATION_DOC}#validation-mode-simple-and-nested`}
                              title="Documentation: Simple and Nested validation"
                            />
                          </Form.Label>
                          <Form.Check
                            type="radio"
                            name="validation-mode"
                            id="validation-mode-simple"
                            label="Simple - select features once on all data, then cross-validate"
                            checked={!isNestedMode}
                            onChange={() => setValidationParams({ mode: 'simple' })}
                          />
                          <Form.Check
                            type="radio"
                            name="validation-mode"
                            id="validation-mode-nested"
                            label={
                              <>
                                Nested - re-select features (and the number of components) inside every fold
                                <HelpLink path={NESTED_DOC} title="Documentation: nested validation" />
                              </>
                            }
                            checked={isNestedMode}
                            disabled={!canUseNested}
                            onChange={() => setValidationParams({ mode: 'nested' })}
                          />
                          {!canUseNested ? (
                            <Form.Text className="text-muted d-block">
                              Enable feature selection (or PLS-DA component search) to use nested
                              validation: without it nested validation equals the ordinary
                              cross-validation.
                            </Form.Text>
                          ) : (
                            <Form.Text className="text-muted d-block">
                              Simple validation overestimates the metrics when features or the number
                              of components are chosen from the data; Nested validation estimates the
                              whole procedure and takes longer.
                            </Form.Text>
                          )}
                          {isNestedMode && (
                            <div className="mt-2">
                              <Form.Text className="d-block">
                                Outer loop: {config.cv_folds} folds over samples
                                (leave-one-sample-out if the number of folds is not less than the
                                number of samples); inner loop: {validationParams.inner_folds ?? 4} folds.
                                {analysisType === 'all' && !fsEnabledForNested &&
                                  ' Without feature selection only PLS-DA is validated by nested CV; the other methods run as Simple.'}
                              </Form.Text>
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-decoration-none"
                                onClick={() => setShowNestedAdvanced(prev => !prev)}
                                aria-expanded={showNestedAdvanced}
                              >
                                <i className={`bi bi-chevron-${showNestedAdvanced ? 'down' : 'right'} me-1`}></i>
                                Advanced
                              </Button>
                              <Collapse in={showNestedAdvanced}>
                                <div>
                                  <Row className="mt-2">
                                    <Col>
                                      <Form.Group>
                                        <Form.Label>
                                          Inner Folds
                                          <HelpLink
                                            path={`${NESTED_DOC}#settings`}
                                            title="Documentation: nested validation settings"
                                          />
                                        </Form.Label>
                                        <Form.Control
                                          type="number"
                                          min="2"
                                          max="10"
                                          value={validationParams.inner_folds ?? 4}
                                          onChange={(e) => setValidationParams({
                                            inner_folds: Math.min(10, Math.max(2, parseInt(e.target.value) || 4))
                                          })}
                                        />
                                        <Form.Text className="text-muted">
                                          Folds of the inner loop that chooses the variant (2-10)
                                        </Form.Text>
                                      </Form.Group>
                                    </Col>
                                    <Col>
                                      <Form.Group>
                                        <Form.Label>
                                          Selection Rule
                                          <HelpLink
                                            path={`${NESTED_DOC}#selection-rule`}
                                            title="Documentation: selection rule"
                                          />
                                        </Form.Label>
                                        <Form.Select
                                          value={validationParams.selection_rule || 'one_se'}
                                          onChange={(e) => setValidationParams({ selection_rule: e.target.value })}
                                        >
                                          <option value="one_se">One-SE - simplest model within one standard error of the best</option>
                                          <option value="best">Best - highest inner balanced accuracy</option>
                                        </Form.Select>
                                      </Form.Group>
                                    </Col>
                                  </Row>
                                </div>
                              </Collapse>
                            </div>
                          )}
                          {nestedSmallSampleWarning && (
                            <Alert variant="warning" className="mt-2 mb-0">
                              <i className="bi bi-exclamation-triangle me-2"></i>
                              {nestedSmallSampleWarning}
                            </Alert>
                          )}
                          {errors.validation && (
                            <Alert variant="danger" className="mt-2 mb-0">{errors.validation}</Alert>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                  )}
                  <Row>
                    <Col>
                      <Form.Group>
                        <Form.Label>
                          CV Method
                          <HelpLink
                            path={`${VALIDATION_DOC}#available-schemes`}
                            title="Documentation: cross-validation schemes"
                          />
                        </Form.Label>
                        <Form.Select
                          value={config.cv_method}
                          onChange={(e) => setConfig({...config, cv_method: e.target.value})}
                        >
                          <option value="group">Group K-Fold</option>
                          <option value="stratified">Stratified K-Fold</option>
                          <option value="kfold">Random K-Fold</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Cross-validation strategy for model evaluation
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group>
                        <Form.Label>
                          Number of Folds
                          <HelpLink
                            path={`${VALIDATION_DOC}#number-of-folds`}
                            title="Documentation: number of folds"
                          />
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max="20"
                          value={config.cv_folds}
                          onChange={(e) => setConfig({ ...config, cv_folds: parseInt(e.target.value) })}
                          disabled={config.cv_method === 'leave_one_out'}
                          isInvalid={!!errors.cv_folds}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.cv_folds}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  {analysisType === 'pipeline' && (
                    <Alert variant="info" className="mt-3">
                      <i className="bi bi-info-circle me-2"></i>
                      For Pipeline analysis, this is the <strong>outer cross-validation</strong> used for final model evaluation.
                      Inner CV for GridSearch is configured in the Pipeline Configuration tab.
                    </Alert>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        {(errors.validation || showEstimate) && (
          <div className="me-auto small" style={{ maxWidth: '70%' }}>
            {errors.validation && (
              <div className="text-danger">{errors.validation}</div>
            )}
            {showEstimate && (
              <div className={estimate.allowed === false ? 'text-danger' : 'text-muted'}>
                <i className="bi bi-clock me-1"></i>
                Estimated: {estimate.n_analyses} {estimate.n_analyses === 1 ? 'analysis' : 'analyses'}
                {' '}&middot; {estimate.n_fits.toLocaleString('en-US')} model fits
                {' '}&middot; about {formatDuration(estimate.wall_seconds[0])}&ndash;{formatDuration(estimate.wall_seconds[1])}
                <HelpLink
                  path={`${ANALYSIS_DIALOG_DOC}#validation-mode-and-the-estimate`}
                  title="Documentation: the run-time estimate"
                />
              </div>
            )}
            {showEstimate && (estimate.warnings || []).map((warning, index) => (
              <div key={index} className={estimate.allowed === false ? 'text-danger' : 'text-warning'}>
                {warning}
              </div>
            ))}
          </div>
        )}
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={estimate?.allowed === false}>
          <i className="bi bi-play-fill me-1"></i>
          Start Analysis
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AnalysisDialog;